// Bookings tab: per-item booking checklist (flights, hotel, activities with
// booking links), live flight search via /api/tp/flights/* (falls back to
// Google Flights links when Amadeus is unconfigured — notConfigured flag),
// and the scan-booking modal (screenshot → /api/tp/scan-screenshot → Claude
// extracts details). Booking state lives in trip.bookings.
import React from 'react';
import { PALETTE, headerPad } from '../theme';
import { Icon, IconBtn, ImageModal } from '../components/UI';
import { buildBookingChecklist, googleFlightsUrl, bookingInfoForIdea } from '../utils/bookingLinks';
import { initTripPacking, fmtDMY } from '../utils/trips';
import { useAuth } from '../contexts/AuthContext';
import { applyFlightScan, removeFlightLeg, updateFlightLeg, sortLegsChrono, applyHotelScan, removeHotelLeg, updateHotelLeg, sortHotelLegsChrono, applyActivityScan, removeActivityLeg, updateActivityLeg, applyInsuranceScan, removeInsuranceLeg, updateInsuranceLeg, applyRoamingScan, removeRoamingLeg, updateRoamingLeg, mapIdeaScanToFields } from '../utils/scanDetails';
import { compressImage } from '../utils/image';
import { isSafeHttpUrl } from '../utils/url';

// ── helpers ───────────────────────────────────────────────────────────────────
function parseDuration(iso) {
  const m = (iso || '').match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!m) return '';
  return [m[1] && `${m[1]}h`, m[2] && `${m[2]}m`].filter(Boolean).join(' ');
}

function fmtTime(dt) { return (dt || '').slice(11, 16); }

function fmtDateShort(dt) {
  if (!dt) return '';
  const [y, mo, d] = dt.slice(0, 10).split('-').map(Number);
  return new Date(y, mo - 1, d).toLocaleDateString('en-SG', { month: 'short', day: 'numeric' });
}

// ISO "YYYY-MM-DD" -> "DD/MM/YYYY (Dow)" for flight/hotel leg dates.
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function fmtLegDate(iso) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  const dow = DOW[new Date(iso + 'T12:00:00').getDay()];
  return `${d}/${mo}/${y} (${dow})`;
}

// Airport IATA code -> timezone abbreviation + standard UTC offset. Covers
// the airports this app already knows about (utils/bookingLinks.js CITY_IATA)
// plus SIN (the app's home base) — "where possible" per the ask, not an
// authoritative IANA timezone database. DST is not modeled; offsets are the
// commonly-cited standard-time value for that city.
const AIRPORT_TZ = {
  SIN: { abbr: 'SGT', offset: 8 }, HKG: { abbr: 'HKT', offset: 8 },
  BCN: { abbr: 'CET', offset: 1 }, MAD: { abbr: 'CET', offset: 1 },
  BRU: { abbr: 'CET', offset: 1 }, CDG: { abbr: 'CET', offset: 1 },
  FCO: { abbr: 'CET', offset: 1 }, AMS: { abbr: 'CET', offset: 1 },
  BER: { abbr: 'CET', offset: 1 }, MUC: { abbr: 'CET', offset: 1 },
  VIE: { abbr: 'CET', offset: 1 }, ZRH: { abbr: 'CET', offset: 1 },
  PRG: { abbr: 'CET', offset: 1 }, LJU: { abbr: 'CET', offset: 1 },
  ZAG: { abbr: 'CET', offset: 1 }, BUD: { abbr: 'CET', offset: 1 },
  IST: { abbr: 'TRT', offset: 3 }, LHR: { abbr: 'GMT', offset: 0 },
  LIS: { abbr: 'WET', offset: 0 }, DPS: { abbr: 'WITA', offset: 8 },
  CGK: { abbr: 'WIB', offset: 7 }, BKK: { abbr: 'ICT', offset: 7 },
  HKT: { abbr: 'ICT', offset: 7 }, CNX: { abbr: 'ICT', offset: 7 },
  HAN: { abbr: 'ICT', offset: 7 }, SGN: { abbr: 'ICT', offset: 7 },
  KUL: { abbr: 'MYT', offset: 8 }, ICN: { abbr: 'KST', offset: 9 },
  NRT: { abbr: 'JST', offset: 9 }, KIX: { abbr: 'JST', offset: 9 },
  TPE: { abbr: 'CST', offset: 8 }, DXB: { abbr: 'GST', offset: 4 },
  SYD: { abbr: 'AEST', offset: 10 }, MEL: { abbr: 'AEST', offset: 10 },
  MLE: { abbr: 'MVT', offset: 5 }, JFK: { abbr: 'EST', offset: -5 },
};
function airportTzLabel(code) {
  const tz = AIRPORT_TZ[(code || '').toUpperCase()];
  if (!tz) return '';
  const sign = tz.offset >= 0 ? '+' : '-';
  return `${tz.abbr}, GMT${sign}${Math.abs(tz.offset)}`;
}

function buildGfUrl(destination, dateStart, dateEnd, travelers) {
  return googleFlightsUrl(destination || '', dateStart, dateEnd, travelers);
}

// ── FlightCard ────────────────────────────────────────────────────────────────
function FlightCard({ offer, destination, dateStart, dateEnd, travelers }) {
  const out = offer.outbound;
  const ret = offer.return;
  const gfUrl = buildGfUrl(destination, dateStart, dateEnd, travelers);

  const LegRow = ({ leg, label }) => (
    <div style={{ marginTop: 8 }}>
      {ret && (
        <div style={{ fontFamily: 'JetBrains Mono', fontSize: 8, color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>
          {label}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 15, color: PALETTE.ink, fontWeight: 500 }}>{fmtTime(leg.dep.at)}</div>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute }}>{leg.dep.iata}</div>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <div style={{ fontFamily: 'Inter', fontSize: 10, color: PALETTE.inkMute }}>
            {parseDuration(leg.duration)} · {leg.stops === 0 ? 'Direct' : `${leg.stops} stop${leg.stops > 1 ? 's' : ''}`}
            {leg.via?.length ? ` (${leg.via.join(', ')})` : ''}
          </div>
          <div style={{ width: '100%', height: 1, background: PALETTE.rule, position: 'relative' }}>
            {leg.stops > 0 && leg.via.map((v, i) => (
              <div key={i} style={{
                position: 'absolute', top: '50%', left: `${((i + 1) / (leg.stops + 1)) * 100}%`,
                transform: 'translate(-50%,-50%)',
                width: 5, height: 5, borderRadius: '50%',
                background: PALETTE.inkMute, border: `1px solid ${PALETTE.paper}`,
              }} />
            ))}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 15, color: PALETTE.ink, fontWeight: 500 }}>
            {fmtTime(leg.arr.at)}
            {leg.dep.at.slice(0, 10) !== leg.arr.at.slice(0, 10) && (
              <sup style={{ fontSize: 8, color: PALETTE.accent, marginLeft: 2 }}>+1</sup>
            )}
          </div>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute }}>{leg.arr.iata}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      background: PALETTE.paper, borderRadius: 10,
      border: `0.5px solid ${PALETTE.rule}`,
      padding: 14, marginBottom: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontFamily: 'Fraunces', fontSize: 16, color: PALETTE.ink, letterSpacing: -0.2 }}>{out.carrierName}</div>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.5, textTransform: 'uppercase', marginTop: 1 }}>
            {out.carrier}{ret ? ` · ${fmtDateShort(out.dep.at)} → ${fmtDateShort(ret.dep.at)}` : ` · ${fmtDateShort(out.dep.at)}`}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'Fraunces', fontSize: 19, color: PALETTE.ink, letterSpacing: -0.4 }}>
            {offer.price.currency} {Math.round(offer.price.total).toLocaleString()}
          </div>
          <div style={{ fontFamily: 'Inter', fontSize: 10, color: PALETTE.inkMute }}>total · all pax</div>
        </div>
      </div>

      <LegRow leg={out} label="Outbound" />
      {ret && <LegRow leg={ret} label="Return" />}

      <a
        href={gfUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          marginTop: 12, padding: '7px 12px', borderRadius: 8,
          background: PALETTE.ink, color: PALETTE.paper,
          fontFamily: 'Inter', fontSize: 12, fontWeight: 600,
          textDecoration: 'none',
        }}
      >
        Book on Google Flights <Icon name="arrow" size={12} />
      </a>
    </div>
  );
}

// ── FlightSearchPanel ─────────────────────────────────────────────────────────
// Bonus live pricing when Amadeus is configured and has real offers — renders
// nothing otherwise (searching/unconfigured/error/no-results all return
// null), since the primary "Add a flight/transport booking" / "Google
// Flights" CTA row in BookingItem already covers those cases without a
// redundant second copy.
function FlightSearchPanel({ trip }) {
  const [status, setStatus] = React.useState('idle'); // idle|searching|done|error|unconfigured
  const [offers, setOffers] = React.useState([]);

  const search = React.useCallback(async () => {
    const city = (trip?.destination || '').split(',')[0].trim();
    if (!city || !trip?.dateStart) { setStatus('error'); return; }
    setStatus('searching');
    try {
      const locR = await fetch(`/api/tp/flights/locations?keyword=${encodeURIComponent(city)}`);
      const locD = await locR.json();
      if (locD.notConfigured) { setStatus('unconfigured'); return; }
      if (!locR.ok) throw new Error(locD.error || 'Location lookup failed');
      const cityLoc = (locD.locations || []).find(l => l.subType === 'CITY') || locD.locations?.[0];
      if (!cityLoc?.iataCode) throw new Error(`No airport found for "${city}"`);

      const ps = new URLSearchParams({
        origin: 'SIN', destination: cityLoc.iataCode,
        date: trip.dateStart, adults: String(trip.travelers || 2),
      });
      if (trip.dateEnd) ps.append('returnDate', trip.dateEnd);
      const flR = await fetch(`/api/tp/flights/search?${ps}`);
      const flD = await flR.json();
      if (flD.notConfigured) { setStatus('unconfigured'); return; }
      if (!flR.ok) throw new Error(flD.error || 'Search failed');
      setOffers(flD.offers || []);
      setStatus('done');
    } catch (err) {
      setStatus('error');
    }
  }, [trip]);

  React.useEffect(() => { search(); }, [search]);

  if (status !== 'done' || offers.length === 0) return null;

  return (
    <div style={{ paddingTop: 12 }}>
      <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
        Live prices · SIN → {trip?.destination?.split(',')[0]} · indicative
      </div>
      {offers.map(offer => (
        <FlightCard
          key={offer.id}
          offer={offer}
          destination={trip?.destination}
          dateStart={trip?.dateStart}
          dateEnd={trip?.dateEnd}
          travelers={trip?.travelers || 2}
        />
      ))}
    </div>
  );
}
function BookingSection({ label, count, children, px }) {
  return (
    <div style={{ padding: `14px ${px}px 0` }}>
      <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: PALETTE.inkMute, marginBottom: 10 }}>
        {label}{count != null ? ` · ${count}` : ''}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {children}
      </div>
    </div>
  );
}

export function BookingScreen({ isMobile, trip, onBack, onDone, onToggleBooked, onSaveDetails, onSavePacking, embedded = false, bookableIdeas = [] }) {
  const topPad = headerPad(isMobile);
  const px = isMobile ? 20 : 28;
  const [packingOpen, setPackingOpen] = React.useState(false);

  const checklist = React.useMemo(() => {
    if (!trip) return [];
    return buildBookingChecklist(trip);
  }, [trip]);

  const bookings = trip?.bookings || {};

  // Determine which idea IDs are placed in the itinerary (vs parking lot)
  const placedIds = React.useMemo(() => {
    const rawPlan = trip?.customPlan;
    if (!rawPlan) return null; // null = auto mode: all selected ideas count as placed
    try {
      const arr = typeof rawPlan === 'string' ? JSON.parse(rawPlan) : rawPlan;
      return new Set(Array.isArray(arr) ? arr.flat() : []);
    } catch (e) { return null; }
  }, [trip?.customPlan]);

  // Split into ordered sections: flights → accommodation → activities →
  // insurance → roaming → admin. Flights, accommodation, insurance, and
  // roaming are all always exactly one item each (any number of
  // legs/stays/policies/plans live inside that item's own legs[] list), so —
  // like "Flights" — each is always present, always one entry point.
  const flightGroup = checklist.filter(i => i.kind === 'flight');
  const accomGroup = checklist.filter(i => i.kind === 'accommodation');
  const activitiesGroup = checklist.filter(i => i.kind === 'activity');
  const insuranceGroup = checklist.filter(i => i.kind === 'insurance');
  const roamingGroup = checklist.filter(i => i.kind === 'roaming');
  // bookableIdeas (computed in ItineraryScreen from requiresBooking/booking-link
  // ideas) is the ONLY source of per-idea booking rows — split by itinerary vs
  // parking lot here. There used to be a second, parallel kind:'ticket' path
  // generated in buildBookingChecklist for the same ideas, which duplicated
  // every row (e.g. Atomium showing twice); that generation was removed.
  const placedBookableIdeas = bookableIdeas.filter(i => !placedIds || placedIds.has(i.id));
  const parkingBookableIdeas = bookableIdeas.filter(i => placedIds && !placedIds.has(i.id));
  const adminGroup = checklist.filter(i => !['flight', 'accommodation', 'activity', 'ticket', 'insurance', 'roaming'].includes(i.kind));

  // Progress counts everything
  const allBookableItems = [...flightGroup, ...accomGroup, ...activitiesGroup, ...insuranceGroup, ...roamingGroup, ...adminGroup];
  const totalItems = allBookableItems.length + bookableIdeas.length;
  const totalBooked =
    allBookableItems.filter(i => bookings[i.id]).length +
    bookableIdeas.filter(i => bookings[i.id]).length;

  // Packing list
  const [packing, setPacking] = React.useState(() => initTripPacking(trip));
  React.useEffect(() => { setPacking(initTripPacking(trip)); }, [trip?.id]); // eslint-disable-line
  const handlePackingChange = (newList) => { setPacking(newList); onSavePacking?.(newList); };
  const packedCount = packing.filter(i => i.checked).length;

  const renderItem = (item) => (
    <BookingItem
      key={item.id}
      item={item}
      checked={!!bookings[item.id]}
      onToggle={(checked) => onToggleBooked(item.id, checked)}
      details={bookings[`${item.id}_details`] || {}}
      onSaveDetails={(det) => onSaveDetails?.(item.id, det)}
      trip={LEG_KINDS.has(item.kind) ? trip : null}
    />
  );

  if (!trip) {
    if (embedded) return null;
    return (
      <div style={{ background: PALETTE.paper, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: 'Fraunces', fontSize: 20, color: PALETTE.inkMute, fontStyle: 'italic' }}>No trip selected.</div>
      </div>
    );
  }

  return (
    <div style={{ background: embedded ? 'transparent' : PALETTE.paper, minHeight: embedded ? 0 : '100vh' }}>
      {!embedded && (
        <div style={{ position: 'sticky', top: 0, zIndex: 4, background: PALETTE.paper, padding: topPad, display: 'flex', flexDirection: 'column', gap: 4, borderBottom: `0.5px solid ${PALETTE.ruleSoft}` }}>
          {isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 36 }}>
              <IconBtn name="arrowL" label="Back" onClick={onBack} />
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase' }}>
                {totalBooked}/{totalItems} booked
              </div>
              <div style={{ width: 36 }} />
            </div>
          )}
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
              <button onClick={onBack} style={{ appearance: 'none', border: 0, cursor: 'pointer', background: 'transparent', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkSoft, padding: 0 }}>
                <Icon name="arrowL" size={16} /> Back
              </button>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase', marginLeft: 'auto' }}>
                {totalBooked}/{totalItems} booked
              </div>
            </div>
          )}
          <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: isMobile ? 32 : 28, fontWeight: 400, letterSpacing: -0.8, color: PALETTE.ink, lineHeight: 1.05, padding: '4px 0 0' }}>Book this trip</div>
          <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkSoft, marginTop: 2 }}>
            {trip.destination}{trip.dateStart ? ` · ${fmtDMY(trip.dateStart)}${trip.dateEnd ? ` – ${fmtDMY(trip.dateEnd)}` : ''}` : ''} · {trip.travelers || 2} travelers
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div style={{ height: 3, background: PALETTE.ruleSoft, marginTop: embedded ? 4 : 0 }}>
        <div style={{ height: '100%', width: `${totalItems > 0 ? (totalBooked / totalItems) * 100 : 0}%`, background: PALETTE.accent, transition: 'width 0.4s ease' }} />
      </div>

      {/* Intro blurb */}
      <div style={{ padding: `14px ${px}px 0` }}>
        <div style={{ padding: 14, background: PALETTE.card, borderRadius: 12, border: `0.5px solid ${PALETTE.ruleSoft}` }}>
          <div style={{ fontFamily: 'Fraunces', fontSize: 14, color: PALETTE.ink, lineHeight: 1.5 }}>
            {totalBooked === totalItems && totalItems > 0
              ? "All done! Your trip is fully booked. Have a wonderful journey."
              : "Tick each item as you book it. Links open the booking site — no middleman, no fees."}
          </div>
        </div>
      </div>

      {/* Flights/Transport */}
      {flightGroup.length > 0 && (
        <BookingSection label="Flights/Transport" px={px}>
          {flightGroup.map(renderItem)}
        </BookingSection>
      )}

      {/* Accommodation */}
      {accomGroup.length > 0 && (
        <BookingSection label="Accommodation" px={px}>
          {accomGroup.map(renderItem)}
        </BookingSection>
      )}

      {/* Activities — one unified item to keep uploading bookings into, plus
          any itinerary-linked activity that's been marked "requires booking". */}
      <BookingSection label="Activities" px={px}>
        {activitiesGroup.map(renderItem)}
        {placedBookableIdeas.map(idea => (
          <BookableIdeaItem
            key={idea.id}
            idea={idea}
            booked={!!bookings[idea.id]}
            onToggleBooked={(v) => onToggleBooked?.(idea.id, v)}
            details={bookings[`${idea.id}_details`] || {}}
            onSaveDetails={(det) => onSaveDetails?.(idea.id, det)}
            bookingInfo={bookingInfoForIdea(idea, trip?.destination)}
          />
        ))}
      </BookingSection>

      {/* Activities in parking lot (selected but not placed in itinerary) */}
      {parkingBookableIdeas.length > 0 && (
        <BookingSection label="Parking lot" count={parkingBookableIdeas.length} px={px}>
          <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute, marginBottom: 10, padding: '0 2px', lineHeight: 1.4 }}>
            These activities are selected but not yet placed in your itinerary.
          </div>
          {parkingBookableIdeas.map(idea => (
            <BookableIdeaItem
              key={idea.id}
              idea={idea}
              booked={!!bookings[idea.id]}
              onToggleBooked={(v) => onToggleBooked?.(idea.id, v)}
              details={bookings[`${idea.id}_details`] || {}}
              onSaveDetails={(det) => onSaveDetails?.(idea.id, det)}
              bookingInfo={bookingInfoForIdea(idea, trip?.destination)}
            />
          ))}
        </BookingSection>
      )}

      {/* Travel Insurance */}
      {insuranceGroup.length > 0 && (
        <BookingSection label="Travel Insurance" px={px}>
          {insuranceGroup.map(renderItem)}
        </BookingSection>
      )}

      {/* Mobile Roaming */}
      {roamingGroup.length > 0 && (
        <BookingSection label="Mobile Roaming" px={px}>
          {roamingGroup.map(renderItem)}
        </BookingSection>
      )}

      {/* Admin items (anything else) */}
      {adminGroup.length > 0 && (
        <BookingSection label="Admin" count={adminGroup.length} px={px}>
          {adminGroup.map(renderItem)}
        </BookingSection>
      )}

      {/* Documents */}
      <BookingSection label="Documents" px={px}>
        <DocumentsSection
          docs={bookings['_documents_details']?.docs || []}
          onSave={(docs) => onSaveDetails?.('_documents', { docs })}
          px={0}
        />
      </BookingSection>

      {/* Packing list — modal trigger */}
      <div style={{ padding: `14px ${px}px 0` }}>
        <button
          onClick={() => setPackingOpen(true)}
          style={{
            appearance: 'none', border: `0.5px solid ${PALETTE.ruleSoft}`, borderRadius: 12,
            background: PALETTE.card, cursor: 'pointer', width: '100%',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 16px', boxSizing: 'border-box',
          }}
        >
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontFamily: 'Fraunces', fontSize: 17, letterSpacing: -0.3, color: PALETTE.ink }}>Packing list</div>
            <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute, marginTop: 2 }}>
              {packing.length > 0 ? `${packedCount}/${packing.length} packed` : 'Tap to manage your packing list'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {packing.length > 0 && (
              <div style={{ width: 48, height: 4, borderRadius: 2, background: PALETTE.ruleSoft, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(packedCount / packing.length) * 100}%`, background: PALETTE.green, transition: 'width 0.3s' }} />
              </div>
            )}
            <Icon name="arrow" size={16} style={{ color: PALETTE.inkMute }} />
          </div>
        </button>
      </div>

      {/* Packing modal */}
      {packingOpen && (
        <div onClick={() => setPackingOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(34,31,26,0.5)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: PALETTE.paper, borderRadius: '24px 24px 0 0', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '12px 20px 0', flexShrink: 0 }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: PALETTE.rule, margin: '0 auto 14px' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontFamily: 'Fraunces', fontSize: 24, letterSpacing: -0.5, color: PALETTE.ink }}>Packing list</div>
                <button onClick={() => setPackingOpen(false)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, fontSize: 20, lineHeight: 1, padding: 4 }}>✕</button>
              </div>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, paddingBottom: 40 }}>
              <PackingSection packing={packing} onChange={handlePackingChange} px={20} alwaysOpen />
            </div>
          </div>
        </div>
      )}

      {!embedded && totalBooked === totalItems && totalItems > 0 && (
        <div style={{ padding: `16px ${px}px 40px` }}>
          <button onClick={onDone} style={{ appearance: 'none', border: 0, cursor: 'pointer', width: '100%', padding: '16px', background: PALETTE.ink, color: PALETTE.paper, borderRadius: 12, fontFamily: 'Inter', fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Icon name="check" size={18} stroke={2.5} /> View trip
          </button>
        </div>
      )}

      <div style={{ height: isMobile ? 40 : 0 }} />
    </div>
  );
}

function linkifyText(text) {
  if (!text) return text;
  const urlPattern = /https?:\/\/[^\s<>]+/g;
  const parts = [];
  let lastIndex = 0;
  const allMatches = [...text.matchAll(urlPattern)];
  for (const m of allMatches) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    const url = m[0];
    parts.push(
      <a key={m.index} href={url} target="_blank" rel="noopener noreferrer"
        style={{ color: PALETTE.accent, textDecoration: 'none' }}>
        {url}
      </a>
    );
    lastIndex = m.index + url.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

function BookableIdeaItem({ idea, booked, onToggleBooked, details = {}, onSaveDetails, bookingInfo = null }) {
  const [expanded, setExpanded] = React.useState(false);
  const [showForm, setShowForm] = React.useState(false);
  const [draft, setDraft] = React.useState(details);
  const [uploading, setUploading] = React.useState(false);
  const [confirmDeleteSS, setConfirmDeleteSS] = React.useState(false);
  const fileRef = React.useRef(null);
  const [lightboxSrc, setLightboxSrc] = React.useState(null);
  const draftRef = React.useRef(draft);

  React.useEffect(() => { setDraft(details); draftRef.current = details; }, [JSON.stringify(details)]); // eslint-disable-line
  React.useEffect(() => { draftRef.current = draft; }, [draft]);

  const hasDetails = Object.values(draft || {}).some(v => v && (typeof v === 'string' ? v.trim() : true));

  const saveDetails = () => { onSaveDetails?.(draft); setShowForm(false); };

  const processFile = React.useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const b64 = await compressImage(file);
      // This used to just store the screenshot with no AI extraction at all —
      // the "auto-fills details" copy below was aspirational, not real. Now
      // mirrors BookingItem.processFile: scan first, merge whatever comes back.
      const base64Raw = b64.split(',')[1];
      const mimeType = file.type || 'image/jpeg';
      let scanned = {};
      try {
        const r = await fetch('/api/tp/scan-screenshot', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageData: base64Raw, mimeType }),
        });
        if (r.ok) scanned = mapIdeaScanToFields(await r.json());
      } catch (e) { /* silent — fall back to plain screenshot */ }
      const newDraft = { ...draftRef.current, ...scanned, screenshot: b64, screenshotName: file.name };
      setDraft(newDraft);
      onSaveDetails?.(newDraft);
      if (!booked) onToggleBooked?.(true);
    } catch (err) { console.error('Screenshot save failed:', err); }
    finally { setUploading(false); }
  }, [onSaveDetails, booked, onToggleBooked]);

  React.useEffect(() => {
    if (!expanded) return;
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          processFile(item.getAsFile());
          break;
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [expanded, processFile]);

  const handleFile = (e) => processFile(e.target.files?.[0]);

  return (
    <div style={{
      background: PALETTE.card, borderRadius: 12,
      border: booked ? `1.5px solid ${PALETTE.green}` : `0.5px solid ${PALETTE.ruleSoft}`,
      overflow: 'hidden', transition: 'border-color 0.2s',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px' }}>
        <button
          onClick={() => onToggleBooked?.(!booked)}
          style={{
            appearance: 'none', border: `1.5px solid ${booked ? PALETTE.green : PALETTE.rule}`,
            background: booked ? PALETTE.green : 'transparent',
            width: 22, height: 22, borderRadius: 999, cursor: 'pointer', flexShrink: 0, marginTop: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
          }}
        >
          {booked && <Icon name="check" size={12} stroke={2.5} style={{ color: '#fff' }} />}
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Fraunces', fontSize: 15, color: PALETTE.ink }}>{idea.title}</div>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.5, marginTop: 2 }}>
            {idea.cat}{idea.area ? ` · ${idea.area}` : ''}
          </div>
          {isSafeHttpUrl(idea.sourceUrl) && (
            <a href={idea.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.accent, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 3 }}>
              🔗 link
            </a>
          )}
          {bookingInfo?.description && (
            <div style={{ fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute, marginTop: 5, lineHeight: 1.4 }}>
              {bookingInfo.description}
            </div>
          )}
        </div>
        <button onClick={() => setExpanded(e => !e)} style={{ appearance: 'none', border: 0, cursor: 'pointer', background: 'transparent', color: PALETTE.inkMute, padding: 4, flexShrink: 0 }}>
          <Icon name={expanded ? 'arrowUp' : 'arrowDn'} size={15} />
        </button>
      </div>

      {expanded && (
        <div style={{ padding: '0 14px 14px 48px' }}>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
          {!hasDetails ? (
            // No details yet — paste zone + form directly
            <>
              {uploading ? (
                <div style={{ border: `1.5px dashed ${PALETTE.rule}`, borderRadius: 8, padding: '13px 14px', background: PALETTE.paperDim, textAlign: 'center', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <div style={{ width: 13, height: 13, borderRadius: '50%', border: `2px solid ${PALETTE.accent}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                  <span style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft }}>Scanning screenshot…</span>
                </div>
              ) : !draft.screenshot ? (
                <div onClick={() => fileRef.current?.click()} style={{ border: `1.5px dashed ${PALETTE.rule}`, borderRadius: 8, padding: '13px 14px', cursor: 'pointer', background: PALETTE.paperDim, textAlign: 'center', marginBottom: 12 }}>
                  <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft }}>Paste screenshot (Ctrl+V)</div>
                  <div style={{ fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute, marginTop: 2 }}>or tap to upload · auto-fills details</div>
                </div>
              ) : (
                <img src={draft.screenshot} alt="screenshot" onClick={() => setLightboxSrc(draft.screenshot)} style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, border: `0.5px solid ${PALETTE.rule}`, cursor: 'pointer', marginBottom: 12, display: 'block' }} />
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[{ key: 'confirmationNo', label: 'Ref / Conf. No.', placeholder: 'e.g. ABC-12345' }, { key: 'bookingUrl', label: 'Booking URL', placeholder: 'https://…' }, { key: 'notes', label: 'Notes', placeholder: 'e.g. Need to bring ID' }].map(f => (
                  <div key={f.key}>
                    <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>{f.label}</div>
                    <input value={draft[f.key] || ''} onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))} placeholder={f.placeholder} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '7px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                ))}
              </div>
              <button onClick={saveDetails} style={{ appearance: 'none', border: 0, cursor: 'pointer', background: PALETTE.ink, color: PALETTE.paper, borderRadius: 8, padding: '8px 14px', fontFamily: 'Inter', fontSize: 12, fontWeight: 600, marginTop: 10 }}>Save</button>
            </>
          ) : !showForm ? (
            // View mode
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 10 }}>
                {draft.confirmationNo && <div style={{ display: 'flex', gap: 8 }}><span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', minWidth: 80, paddingTop: 1 }}>Ref / Conf.</span><span style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink }}>{draft.confirmationNo}</span></div>}
                {draft.bookingUrl && <div style={{ display: 'flex', gap: 8 }}><span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', minWidth: 80, paddingTop: 1 }}>Link</span><a href={draft.bookingUrl} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.accent, textDecoration: 'none', wordBreak: 'break-all' }}>{draft.bookingUrl}</a></div>}
                {draft.notes && <div style={{ display: 'flex', gap: 8 }}><span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', minWidth: 80, paddingTop: 1 }}>Notes</span><span style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink }}>{linkifyText(draft.notes)}</span></div>}
                {draft.screenshot && (
                  <div style={{ marginTop: 4 }}>
                    <img src={draft.screenshot} alt="screenshot" onClick={() => setLightboxSrc(draft.screenshot)} style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, border: `0.5px solid ${PALETTE.rule}`, cursor: 'pointer', display: 'block' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
                      {confirmDeleteSS ? (
                        <>
                          <span style={{ fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkSoft }}>Delete screenshot?</span>
                          <button onClick={() => { const nd = { ...draftRef.current }; delete nd.screenshot; delete nd.screenshotName; setDraft(nd); onSaveDetails?.(nd); setConfirmDeleteSS(false); }} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'Inter', fontSize: 11, color: PALETTE.red, padding: 0, fontWeight: 600 }}>Delete</button>
                          <button onClick={() => setConfirmDeleteSS(false)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute, padding: 0 }}>Cancel</button>
                        </>
                      ) : (
                        <button onClick={() => setConfirmDeleteSS(true)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute, padding: 0, display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Icon name="x" size={11} /> Delete screenshot
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <button onClick={() => { setDraft(details); setShowForm(true); }} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', padding: 0, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Icon name="pencil" size={12} /> Edit details
                </button>
                <span style={{ color: PALETTE.inkMute, fontSize: 12 }}>·</span>
                <button onClick={() => fileRef.current?.click()} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', padding: 0, fontFamily: 'Inter', fontSize: 12, color: PALETTE.accent, display: 'flex', alignItems: 'center', gap: 5 }}>
                  {draft.screenshot ? 'Replace screenshot' : 'Add screenshot'}
                </button>
              </div>
            </>
          ) : (
            // Edit mode
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[{ key: 'confirmationNo', label: 'Ref / Conf. No.', placeholder: 'e.g. ABC-12345' }, { key: 'bookingUrl', label: 'Booking URL', placeholder: 'https://…' }, { key: 'notes', label: 'Notes', placeholder: 'e.g. Need to bring ID' }].map(f => (
                <div key={f.key}>
                  <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>{f.label}</div>
                  <input value={draft[f.key] || ''} onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))} placeholder={f.placeholder} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '7px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button onClick={saveDetails} style={{ appearance: 'none', border: 0, cursor: 'pointer', background: PALETTE.ink, color: PALETTE.paper, borderRadius: 8, padding: '8px 14px', fontFamily: 'Inter', fontSize: 12, fontWeight: 600 }}>Save</button>
                <button onClick={() => setShowForm(false)} style={{ appearance: 'none', border: 0, cursor: 'pointer', background: 'transparent', color: PALETTE.inkMute, fontFamily: 'Inter', fontSize: 12, padding: '8px 6px' }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
      {lightboxSrc && <ImageModal src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
}

// Flights, accommodation, activities, insurance, and roaming are all
// leg-based (bookings.<id>_details.legs) and render through their own
// dedicated scan-panel + legs-list UI in BookingItem, not this generic
// field list — see LEG_KINDS.
const BOOKING_DETAIL_FIELDS = {};
const BOOKING_GENERIC_FIELDS = [
  { key: 'confirmation', label: 'Ref / Conf. No.', placeholder: 'e.g. ABC-12345' },
  { key: 'bookingUrl', label: 'Booking URL', placeholder: 'https://…' },
  { key: 'notes', label: 'Notes', placeholder: '' },
];

// Item kinds whose detail is a chronological legs[] list (one flight leg /
// one hotel stay per entry) with their own scan panel, rather than the
// generic single-record field form.
const LEG_KINDS = new Set(['flight', 'accommodation', 'activity', 'insurance', 'roaming']);

// Secondary CTA next to the primary "Add a … booking" button (Google Flights,
// Booking.com, etc.) — deliberately smaller/outlined so it reads as the
// backup option, not the main action.
function SecondaryLink({ link }) {
  if (!link) return null;
  return (
    <a href={link.url} target="_blank" rel="noopener noreferrer" style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '8px 12px', borderRadius: 8, textDecoration: 'none',
      background: 'transparent', color: PALETTE.inkSoft,
      fontFamily: 'Inter', fontSize: 12, fontWeight: 500,
      border: `1px solid ${PALETTE.rule}`, whiteSpace: 'nowrap',
    }}>
      {link.label} <Icon name="arrow" size={11} />
    </a>
  );
}

function FlightScanPanel({ onFill, existing, secondaryLink }) {
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState('screenshot'); // 'screenshot' | 'text' | 'manual'
  const [text, setText] = React.useState('');
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [result, setResult] = React.useState(null);
  const [file, setFile] = React.useState(null);
  // Ref/passenger are per-leg (see applyFlightScan) — a screenshot/text scan
  // picks them up automatically from the document itself, no manual typing
  // needed. Only the manual-entry tab needs its own fields, prefilled from
  // the most recently added leg for "same ref, one less retype".
  const lastLeg = existing?.legs?.[existing.legs.length - 1];
  const blankManual = { flightNo: '', airline: '', date: '', depTime: '', origin: '', arrTime: '', dest: '', confirmation: lastLeg?.confirmation || '', passengerName: lastLeg?.passengerName || '' };
  const [manual, setManual] = React.useState(blankManual);
  const fileRef = React.useRef(null);

  const resetAll = () => {
    setError(''); setText(''); setFile(null); setPreview(null); setManual(blankManual); setResult(null);
  };

  const applyAndClose = (raw) => {
    onFill(applyFlightScan(existing || {}, raw));
    setOpen(false); resetAll();
  };

  // Selecting a file just stages it (with a preview) — scanning happens on
  // "Extract details", so there's always a review step before anything gets added.
  const handleFile = React.useCallback((f) => {
    if (!f) return;
    setFile(f); setResult(null); setError('');
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  }, []);

  React.useEffect(() => {
    if (!open || mode !== 'screenshot') return;
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          handleFile(item.getAsFile());
          break;
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open, mode, handleFile]);

  const handleScan = async () => {
    setLoading(true); setError(''); setResult(null);
    try {
      let data;
      if (mode === 'screenshot') {
        if (!file) { setError('Please upload a screenshot.'); setLoading(false); return; }
        const base64 = await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = (ev) => res(ev.target.result.split(',')[1]);
          reader.onerror = rej;
          reader.readAsDataURL(file);
        });
        const token = user ? await user.getIdToken() : '';
        const r = await fetch('/api/tp/scan-screenshot', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ imageData: base64, mimeType: file.type || 'image/jpeg' }),
        });
        data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Scan failed');
      } else {
        if (!text.trim()) { setError('Please paste your flight details.'); setLoading(false); return; }
        const r = await fetch('/api/tp/scan-text', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
        });
        data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Extraction failed');
      }
      setResult(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const applyResult = () => { if (result) applyAndClose(result); };

  const canApplyManual = manual.flightNo.trim() || manual.date.trim();
  const applyManual = () => {
    if (!canApplyManual) return;
    applyAndClose({
      airline: manual.airline.trim(),
      bookingRef: manual.confirmation.trim(), passengerName: manual.passengerName.trim(),
      legs: [{
        flightNo: manual.flightNo.trim(), date: manual.date.trim(),
        depTime: manual.depTime.trim(), arrTime: manual.arrTime.trim(),
        depAirport: manual.origin.trim().toUpperCase(), arrAirport: manual.dest.trim().toUpperCase(),
      }],
    });
  };

  if (!open) {
    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <button
          onClick={() => setOpen(true)}
          style={{ appearance: 'none', border: 0, background: PALETTE.ink, color: PALETTE.paper, cursor: 'pointer', borderRadius: 8, padding: '8px 14px', fontFamily: 'Inter', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Icon name="plus" size={13} /> Add a flight/transport booking
        </button>
        <SecondaryLink link={secondaryLink} />
      </div>
    );
  }

  return (
    <div style={{ background: PALETTE.paperDim, borderRadius: 12, border: `0.5px solid ${PALETTE.rule}`, padding: '14px 16px', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: PALETTE.inkMute }}>Add a flight/transport booking</div>
        <button onClick={() => { setOpen(false); resetAll(); }} style={{ appearance: 'none', border: 0, background: 'transparent', color: PALETTE.inkMute, cursor: 'pointer', fontSize: 14, padding: 0 }}>✕</button>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[['screenshot', '📷 Screenshot'], ['text', '📝 Paste text'], ['manual', '✏️ Manual']].map(([key, label]) => (
          <button key={key} onClick={() => { setMode(key); setError(''); setResult(null); }} style={{ appearance: 'none', cursor: 'pointer', padding: '5px 12px', borderRadius: 999, background: mode === key ? PALETTE.ink : 'transparent', color: mode === key ? PALETTE.paper : PALETTE.inkSoft, fontFamily: 'Inter', fontSize: 11, fontWeight: 500, border: mode === key ? '1px solid transparent' : `1px solid ${PALETTE.rule}` }}>{label}</button>
        ))}
      </div>

      {mode === 'screenshot' && (
        <div onClick={() => fileRef.current?.click()} style={{ border: `1.5px dashed ${PALETTE.rule}`, borderRadius: 10, padding: '16px', textAlign: 'center', cursor: 'pointer', marginBottom: 10, background: PALETTE.card }}>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files?.[0])} />
          {preview ? (
            <img src={preview} alt="preview" style={{ maxWidth: '100%', maxHeight: 140, borderRadius: 8, objectFit: 'contain' }} />
          ) : (
            <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute }}>Tap to upload, or paste (Ctrl+V) a screenshot of your e-ticket or booking email</div>
          )}
        </div>
      )}

      {mode === 'text' && (
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Paste your e-ticket text, booking confirmation, or any flight details here…"
          rows={4}
          style={{ width: '100%', boxSizing: 'border-box', appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '9px 12px', fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, background: PALETTE.paper, outline: 'none', resize: 'none', lineHeight: 1.5, marginBottom: 10 }}
        />
      )}

      {mode === 'manual' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
          {[
            { key: 'flightNo', label: 'Flight No.', ph: 'e.g. SQ469', flex: 1 },
            { key: 'airline', label: 'Airline', ph: 'e.g. Singapore Airlines', flex: 1 },
          ].map(f => (
            <div key={f.key}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>{f.label}</div>
              <input value={manual[f.key]} onChange={e => setManual(m => ({ ...m, [f.key]: e.target.value }))} placeholder={f.ph} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
            </div>
          ))}
          <div>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Date</div>
            <input type="date" value={manual.date} onChange={e => setManual(m => ({ ...m, date: e.target.value }))} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Departs</div>
              <input type="time" value={manual.depTime} onChange={e => setManual(m => ({ ...m, depTime: e.target.value }))} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>From (airport)</div>
              <input value={manual.origin} onChange={e => setManual(m => ({ ...m, origin: e.target.value }))} placeholder="e.g. SIN" style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box', textTransform: 'uppercase' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Arrives</div>
              <input type="time" value={manual.arrTime} onChange={e => setManual(m => ({ ...m, arrTime: e.target.value }))} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>To (airport)</div>
              <input value={manual.dest} onChange={e => setManual(m => ({ ...m, dest: e.target.value }))} placeholder="e.g. NRT" style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box', textTransform: 'uppercase' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Booking Ref / PNR</div>
              <input value={manual.confirmation} onChange={e => setManual(m => ({ ...m, confirmation: e.target.value }))} placeholder="e.g. ABC123" style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Passenger(s)</div>
              <input value={manual.passengerName} onChange={e => setManual(m => ({ ...m, passengerName: e.target.value }))} placeholder="e.g. Joseph Lee" style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
        </div>
      )}

      {error && <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.red, marginBottom: 8 }}>{error}</div>}

      {result && !error && mode !== 'manual' && (() => {
        const rLegs = Array.isArray(result.legs) ? result.legs : [];
        const LegRow = ({ flt, dep, depAp, arr, arrAp, date }) => (dep || arr) ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, padding: '5px 8px', background: PALETTE.paperDim, borderRadius: 6, flexWrap: 'wrap' }}>
            {flt && <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkSoft, minWidth: 44 }}>{flt}</span>}
            <span style={{ fontFamily: 'JetBrains Mono', fontSize: 13, color: PALETTE.ink, fontWeight: 700 }}>{dep || '—'}</span>
            {depAp && <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute }}>{depAp}</span>}
            <span style={{ color: PALETTE.inkMute }}>→</span>
            <span style={{ fontFamily: 'JetBrains Mono', fontSize: 13, color: PALETTE.ink, fontWeight: 700 }}>{arr || '—'}</span>
            {arrAp && <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute }}>{arrAp}</span>}
            {date && <span style={{ fontFamily: 'Inter', fontSize: 10, color: PALETTE.inkMute, marginLeft: 'auto' }}>{fmtLegDate(date)}</span>}
          </div>
        ) : null;
        return (
          <div style={{ background: PALETTE.paper, borderRadius: 8, padding: '10px 12px', marginBottom: 10, border: `0.5px solid ${PALETTE.rule}` }}>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.green, marginBottom: 6 }}>
              ✓ Extracted{rLegs.length > 1 ? ` · ${rLegs.length} segments` : ''}
            </div>
            {result.airline && <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, fontWeight: 500, marginBottom: 6 }}>✈ {result.airline}</div>}
            {rLegs.map((seg, i) => (
              <LegRow key={i} flt={seg.flightNo} dep={seg.depTime} depAp={seg.depAirport} arr={seg.arrTime} arrAp={seg.arrAirport} date={seg.date} />
            ))}
            {rLegs.length === 0 && result.flightDetails && <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, marginBottom: 4, lineHeight: 1.4 }}>{result.flightDetails}</div>}
            {result.bookingRef && <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, marginTop: 4 }}>Ref: {result.bookingRef}</div>}
          </div>
        );
      })()}

      {mode === 'manual' ? (
        <button onClick={applyManual} disabled={!canApplyManual} style={{ appearance: 'none', border: 0, cursor: canApplyManual ? 'pointer' : 'default', width: '100%', padding: '10px', background: canApplyManual ? PALETTE.accent : PALETTE.rule, color: canApplyManual ? '#fff' : PALETTE.inkMute, borderRadius: 8, fontFamily: 'Inter', fontSize: 13, fontWeight: 600 }}>
          Add flight
        </button>
      ) : !result ? (
        <button onClick={handleScan} disabled={loading} style={{ appearance: 'none', border: 0, cursor: loading ? 'default' : 'pointer', width: '100%', padding: '10px', background: loading ? PALETTE.rule : PALETTE.ink, color: loading ? PALETTE.inkMute : PALETTE.paper, borderRadius: 8, fontFamily: 'Inter', fontSize: 13, fontWeight: 600 }}>
          {loading ? 'Extracting…' : 'Extract details'}
        </button>
      ) : (
        <button onClick={applyResult} style={{ appearance: 'none', border: 0, cursor: 'pointer', width: '100%', padding: '10px', background: PALETTE.accent, color: '#fff', borderRadius: 8, fontFamily: 'Inter', fontSize: 13, fontWeight: 600 }}>
          Add flight
        </button>
      )}
    </div>
  );
}

// ── HotelScanPanel ────────────────────────────────────────────────────────────
// Mirrors FlightScanPanel: collapsed = primary "Add a hotel booking" button +
// a secondary discovery link; expanded = screenshot/text/manual entry. Unlike
// flights there's no shared ref/passenger row — every stay is self-contained
// (own confirmation), so nothing here can go stale when a stay is removed.
function HotelScanPanel({ onFill, existing, secondaryLink }) {
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState('screenshot'); // 'screenshot' | 'text' | 'manual'
  const [text, setText] = React.useState('');
  const [file, setFile] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [result, setResult] = React.useState(null);
  const [manual, setManual] = React.useState({ hotelName: '', checkIn: '', checkOut: '', confirmation: '' });
  const fileRef = React.useRef(null);

  const resetAll = () => {
    setError(''); setText(''); setFile(null); setPreview(null); setResult(null);
    setManual({ hotelName: '', checkIn: '', checkOut: '', confirmation: '' });
  };

  const applyAndClose = (raw) => {
    onFill(applyHotelScan(existing || {}, raw));
    setOpen(false); resetAll();
  };

  const handleFile = React.useCallback((f) => {
    if (!f) return;
    setFile(f); setResult(null); setError('');
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  }, []);

  React.useEffect(() => {
    if (!open || mode !== 'screenshot') return;
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          handleFile(item.getAsFile());
          break;
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open, mode, handleFile]);

  const handleScan = async () => {
    setLoading(true); setError(''); setResult(null);
    try {
      let data;
      if (mode === 'screenshot') {
        if (!file) { setError('Please upload a screenshot.'); setLoading(false); return; }
        const base64 = await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = (ev) => res(ev.target.result.split(',')[1]);
          reader.onerror = rej;
          reader.readAsDataURL(file);
        });
        const token = user ? await user.getIdToken() : '';
        const r = await fetch('/api/tp/scan-screenshot', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ imageData: base64, mimeType: file.type || 'image/jpeg' }),
        });
        data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Scan failed');
      } else {
        if (!text.trim()) { setError('Please paste your hotel booking details.'); setLoading(false); return; }
        const r = await fetch('/api/tp/scan-text', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
        });
        data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Extraction failed');
      }
      setResult(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const applyResult = () => { if (result) applyAndClose(result); };

  const canApplyManual = manual.hotelName.trim() || manual.checkIn.trim();
  const applyManual = () => {
    if (!canApplyManual) return;
    applyAndClose({
      hotelName: manual.hotelName.trim(), checkInDate: manual.checkIn.trim(),
      checkOutDate: manual.checkOut.trim(), bookingRef: manual.confirmation.trim(),
    });
  };

  if (!open) {
    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <button
          onClick={() => setOpen(true)}
          style={{ appearance: 'none', border: 0, background: PALETTE.ink, color: PALETTE.paper, cursor: 'pointer', borderRadius: 8, padding: '8px 14px', fontFamily: 'Inter', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Icon name="plus" size={13} /> Add a hotel booking
        </button>
        <SecondaryLink link={secondaryLink} />
      </div>
    );
  }

  return (
    <div style={{ background: PALETTE.paperDim, borderRadius: 12, border: `0.5px solid ${PALETTE.rule}`, padding: '14px 16px', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: PALETTE.inkMute }}>Add a hotel booking</div>
        <button onClick={() => { setOpen(false); resetAll(); }} style={{ appearance: 'none', border: 0, background: 'transparent', color: PALETTE.inkMute, cursor: 'pointer', fontSize: 14, padding: 0 }}>✕</button>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[['screenshot', '📷 Screenshot'], ['text', '📝 Paste text'], ['manual', '✏️ Manual']].map(([key, label]) => (
          <button key={key} onClick={() => { setMode(key); setError(''); setResult(null); }} style={{ appearance: 'none', cursor: 'pointer', padding: '5px 12px', borderRadius: 999, background: mode === key ? PALETTE.ink : 'transparent', color: mode === key ? PALETTE.paper : PALETTE.inkSoft, fontFamily: 'Inter', fontSize: 11, fontWeight: 500, border: mode === key ? '1px solid transparent' : `1px solid ${PALETTE.rule}` }}>{label}</button>
        ))}
      </div>

      {mode === 'screenshot' && (
        <div onClick={() => fileRef.current?.click()} style={{ border: `1.5px dashed ${PALETTE.rule}`, borderRadius: 10, padding: '16px', textAlign: 'center', cursor: 'pointer', marginBottom: 10, background: PALETTE.card }}>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files?.[0])} />
          {preview ? (
            <img src={preview} alt="preview" style={{ maxWidth: '100%', maxHeight: 140, borderRadius: 8, objectFit: 'contain' }} />
          ) : (
            <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute }}>Tap to upload, or paste (Ctrl+V) a screenshot of your booking confirmation</div>
          )}
        </div>
      )}

      {mode === 'text' && (
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Paste your hotel confirmation email or booking details here…"
          rows={4}
          style={{ width: '100%', boxSizing: 'border-box', appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '9px 12px', fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, background: PALETTE.paper, outline: 'none', resize: 'none', lineHeight: 1.5, marginBottom: 10 }}
        />
      )}

      {mode === 'manual' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
          {[
            { key: 'hotelName', label: 'Hotel', ph: 'e.g. Four Seasons Bali' },
            { key: 'confirmation', label: 'Confirmation No.', ph: 'e.g. HB-20260001' },
          ].map(f => (
            <div key={f.key}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>{f.label}</div>
              <input value={manual[f.key]} onChange={e => setManual(m => ({ ...m, [f.key]: e.target.value }))} placeholder={f.ph} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Check-in</div>
              <input type="date" value={manual.checkIn} onChange={e => setManual(m => ({ ...m, checkIn: e.target.value }))} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Check-out</div>
              <input type="date" value={manual.checkOut} onChange={e => setManual(m => ({ ...m, checkOut: e.target.value }))} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
        </div>
      )}

      {error && <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.red, marginBottom: 8 }}>{error}</div>}

      {result && !error && mode !== 'manual' && (
        <div style={{ background: PALETTE.paper, borderRadius: 8, padding: '10px 12px', marginBottom: 10, border: `0.5px solid ${PALETTE.rule}` }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.green, marginBottom: 6 }}>✓ Extracted</div>
          {result.hotelName && <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, fontWeight: 500, marginBottom: 4 }}>🏨 {result.hotelName}</div>}
          {(result.checkInDate || result.checkOutDate) && (
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: PALETTE.inkSoft }}>{fmtLegDate(result.checkInDate) || '?'} → {fmtLegDate(result.checkOutDate) || '?'}</div>
          )}
          {!result.hotelName && result.hotelDetails && <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, lineHeight: 1.4 }}>{result.hotelDetails}</div>}
          {result.bookingRef && <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, marginTop: 4 }}>Ref: {result.bookingRef}</div>}
        </div>
      )}

      {mode === 'manual' ? (
        <button onClick={applyManual} disabled={!canApplyManual} style={{ appearance: 'none', border: 0, cursor: canApplyManual ? 'pointer' : 'default', width: '100%', padding: '10px', background: canApplyManual ? PALETTE.accent : PALETTE.rule, color: canApplyManual ? '#fff' : PALETTE.inkMute, borderRadius: 8, fontFamily: 'Inter', fontSize: 13, fontWeight: 600 }}>
          Add hotel
        </button>
      ) : !result ? (
        <button onClick={handleScan} disabled={loading} style={{ appearance: 'none', border: 0, cursor: loading ? 'default' : 'pointer', width: '100%', padding: '10px', background: loading ? PALETTE.rule : PALETTE.ink, color: loading ? PALETTE.inkMute : PALETTE.paper, borderRadius: 8, fontFamily: 'Inter', fontSize: 13, fontWeight: 600 }}>
          {loading ? 'Extracting…' : 'Extract details'}
        </button>
      ) : (
        <button onClick={applyResult} style={{ appearance: 'none', border: 0, cursor: 'pointer', width: '100%', padding: '10px', background: PALETTE.accent, color: '#fff', borderRadius: 8, fontFamily: 'Inter', fontSize: 13, fontWeight: 600 }}>
          Add hotel
        </button>
      )}
    </div>
  );
}

// ── ActivityScanPanel ────────────────────────────────────────────────────────
// Same pattern again — collapsed = primary "Add an activity booking" button,
// no secondary link (no GetYourGuide/Viator; this is for filing bookings you
// already made, not discovering new ones). Every booking is its own
// self-contained leg (title/notes/confirmation/screenshot).
function ActivityScanPanel({ onFill, existing }) {
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState('screenshot'); // 'screenshot' | 'text' | 'manual'
  const [text, setText] = React.useState('');
  const [file, setFile] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const blankManual = { title: '', date: '', time: '', confirmation: '', notes: '' };
  const [manual, setManual] = React.useState(blankManual);
  // The scan API only returns free text for activities (no reliably clean
  // short name) — so after a scan/extract, the result lands here as an
  // EDITABLE draft (prefilled with whatever activityName/activityDate/
  // activityTime it found) rather than being applied straight away. That's
  // what lets you fix a wrong/blank name before it's saved.
  const [draft, setDraft] = React.useState(null);
  const [scanned, setScanned] = React.useState(null); // raw scan result, for its screenshotUrl
  const fileRef = React.useRef(null);

  const resetAll = () => {
    setError(''); setText(''); setFile(null); setPreview(null); setManual(blankManual);
    setDraft(null); setScanned(null);
  };

  const applyAndClose = (raw) => {
    onFill(applyActivityScan(existing || {}, raw));
    setOpen(false); resetAll();
  };

  const handleFile = React.useCallback((f) => {
    if (!f) return;
    setFile(f); setDraft(null); setScanned(null); setError('');
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  }, []);

  React.useEffect(() => {
    if (!open || mode !== 'screenshot') return;
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          handleFile(item.getAsFile());
          break;
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open, mode, handleFile]);

  const seedDraftFromResult = (data) => {
    setScanned(data);
    setDraft({
      title: data.activityName || '', date: data.activityDate || '', time: data.activityTime || '',
      confirmation: data.bookingRef || '', notes: data.activityDetails || data.summary || '',
    });
  };

  const handleScan = async () => {
    setLoading(true); setError(''); setDraft(null);
    try {
      let data;
      if (mode === 'screenshot') {
        if (!file) { setError('Please upload a screenshot.'); setLoading(false); return; }
        const base64 = await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = (ev) => res(ev.target.result.split(',')[1]);
          reader.onerror = rej;
          reader.readAsDataURL(file);
        });
        const token = user ? await user.getIdToken() : '';
        const r = await fetch('/api/tp/scan-screenshot', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ imageData: base64, mimeType: file.type || 'image/jpeg' }),
        });
        data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Scan failed');
      } else {
        if (!text.trim()) { setError('Please paste your booking details.'); setLoading(false); return; }
        const r = await fetch('/api/tp/scan-text', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
        });
        data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Extraction failed');
      }
      seedDraftFromResult(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const canApplyDraft = draft?.title.trim();
  const applyDraft = () => {
    if (!canApplyDraft) return;
    applyAndClose({
      activityName: draft.title.trim(), activityDate: draft.date.trim(), activityTime: draft.time.trim(),
      bookingRef: draft.confirmation.trim(), notes: draft.notes.trim(),
      screenshotUrl: scanned?.screenshotUrl,
    });
  };

  const canApplyManual = manual.title.trim();
  const applyManual = () => {
    if (!canApplyManual) return;
    applyAndClose({
      activityName: manual.title.trim(), activityDate: manual.date.trim(), activityTime: manual.time.trim(),
      bookingRef: manual.confirmation.trim(), notes: manual.notes.trim(),
    });
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{ appearance: 'none', border: 0, background: PALETTE.ink, color: PALETTE.paper, cursor: 'pointer', borderRadius: 8, padding: '8px 14px', fontFamily: 'Inter', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}
      >
        <Icon name="plus" size={13} /> Add an activity booking
      </button>
    );
  }

  const draftFields = [
    { key: 'title', label: 'What is it?', ph: 'e.g. Sagrada Familia tour' },
    { key: 'confirmation', label: 'Confirmation No.', ph: 'e.g. AB-20260001' },
    { key: 'notes', label: 'Notes', ph: '' },
  ];

  return (
    <div style={{ background: PALETTE.paperDim, borderRadius: 12, border: `0.5px solid ${PALETTE.rule}`, padding: '14px 16px', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: PALETTE.inkMute }}>Add an activity booking</div>
        <button onClick={() => { setOpen(false); resetAll(); }} style={{ appearance: 'none', border: 0, background: 'transparent', color: PALETTE.inkMute, cursor: 'pointer', fontSize: 14, padding: 0 }}>✕</button>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[['screenshot', '📷 Screenshot'], ['text', '📝 Paste text'], ['manual', '✏️ Manual']].map(([key, label]) => (
          <button key={key} onClick={() => { setMode(key); setError(''); setDraft(null); }} style={{ appearance: 'none', cursor: 'pointer', padding: '5px 12px', borderRadius: 999, background: mode === key ? PALETTE.ink : 'transparent', color: mode === key ? PALETTE.paper : PALETTE.inkSoft, fontFamily: 'Inter', fontSize: 11, fontWeight: 500, border: mode === key ? '1px solid transparent' : `1px solid ${PALETTE.rule}` }}>{label}</button>
        ))}
      </div>

      {mode === 'screenshot' && (
        <div onClick={() => fileRef.current?.click()} style={{ border: `1.5px dashed ${PALETTE.rule}`, borderRadius: 10, padding: '16px', textAlign: 'center', cursor: 'pointer', marginBottom: 10, background: PALETTE.card }}>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files?.[0])} />
          {preview ? (
            <img src={preview} alt="preview" style={{ maxWidth: '100%', maxHeight: 140, borderRadius: 8, objectFit: 'contain' }} />
          ) : (
            <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute }}>Tap to upload, or paste (Ctrl+V) a screenshot of your ticket or confirmation</div>
          )}
        </div>
      )}

      {mode === 'text' && (
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Paste your booking confirmation email or details here…"
          rows={4}
          style={{ width: '100%', boxSizing: 'border-box', appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '9px 12px', fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, background: PALETTE.paper, outline: 'none', resize: 'none', lineHeight: 1.5, marginBottom: 10 }}
        />
      )}

      {mode === 'manual' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
          <div>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>What is it?</div>
            <input value={manual.title} onChange={e => setManual(m => ({ ...m, title: e.target.value }))} placeholder="e.g. Sagrada Familia tour" style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Date</div>
              <input type="date" value={manual.date} onChange={e => setManual(m => ({ ...m, date: e.target.value }))} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Time</div>
              <input type="time" value={manual.time} onChange={e => setManual(m => ({ ...m, time: e.target.value }))} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Confirmation No.</div>
            <input value={manual.confirmation} onChange={e => setManual(m => ({ ...m, confirmation: e.target.value }))} placeholder="e.g. AB-20260001" style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Notes</div>
            <input value={manual.notes} onChange={e => setManual(m => ({ ...m, notes: e.target.value }))} placeholder="" style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
          </div>
        </div>
      )}

      {error && <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.red, marginBottom: 8 }}>{error}</div>}

      {/* Extracted draft — editable, since the scan can only guess a name/date/time */}
      {draft && !error && mode !== 'manual' && (
        <div style={{ background: PALETTE.paper, borderRadius: 8, padding: '10px 12px', marginBottom: 10, border: `0.5px solid ${PALETTE.rule}` }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.green, marginBottom: 8 }}>✓ Extracted — check/edit before adding</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>What is it?</div>
              <input value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} placeholder="e.g. Sagrada Familia tour" style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paperDim, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Date</div>
                <input type="date" value={draft.date} onChange={e => setDraft(d => ({ ...d, date: e.target.value }))} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paperDim, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Time</div>
                <input type="time" value={draft.time} onChange={e => setDraft(d => ({ ...d, time: e.target.value }))} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paperDim, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>
            {draftFields.slice(1).map(f => (
              <div key={f.key}>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>{f.label}</div>
                <input value={draft[f.key]} onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))} placeholder={f.ph} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paperDim, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === 'manual' ? (
        <button onClick={applyManual} disabled={!canApplyManual} style={{ appearance: 'none', border: 0, cursor: canApplyManual ? 'pointer' : 'default', width: '100%', padding: '10px', background: canApplyManual ? PALETTE.accent : PALETTE.rule, color: canApplyManual ? '#fff' : PALETTE.inkMute, borderRadius: 8, fontFamily: 'Inter', fontSize: 13, fontWeight: 600 }}>
          Add booking
        </button>
      ) : !draft ? (
        <button onClick={handleScan} disabled={loading} style={{ appearance: 'none', border: 0, cursor: loading ? 'default' : 'pointer', width: '100%', padding: '10px', background: loading ? PALETTE.rule : PALETTE.ink, color: loading ? PALETTE.inkMute : PALETTE.paper, borderRadius: 8, fontFamily: 'Inter', fontSize: 13, fontWeight: 600 }}>
          {loading ? 'Extracting…' : 'Extract details'}
        </button>
      ) : (
        <button onClick={applyDraft} disabled={!canApplyDraft} style={{ appearance: 'none', border: 0, cursor: canApplyDraft ? 'pointer' : 'default', width: '100%', padding: '10px', background: canApplyDraft ? PALETTE.accent : PALETTE.rule, color: canApplyDraft ? '#fff' : PALETTE.inkMute, borderRadius: 8, fontFamily: 'Inter', fontSize: 13, fontWeight: 600 }}>
          Add booking
        </button>
      )}
    </div>
  );
}

// ── Insurance / roaming scan panels ──────────────────────────────────────────
// Same "Add a … booking" primary CTA + screenshot/paste-text/manual + extract
// → editable draft → confirm flow as ActivityScanPanel, generalized so a 4th
// and 5th near-identical ~230-line component didn't need writing out by hand.
// Field set and how the AI scan result maps onto it are the only per-kind
// difference — everything else (mode tabs, paste handler, draft review) is
// shared.
const INSURANCE_SCAN_CONFIG = {
  addLabel: 'Add an insurance booking',
  requiredKey: 'provider',
  draftFields: [
    { key: 'provider', label: 'Provider', ph: 'e.g. FWD, NTUC Income' },
    { key: 'policyNumber', label: 'Policy No.', ph: 'e.g. TRV-20260001' },
    { key: 'hotline', label: 'Emergency Hotline', ph: 'e.g. +65 6322 2888' },
    { key: 'coverage', label: 'Coverage', ph: 'e.g. Medical up to $500K, trip cancellation' },
    { key: 'notes', label: 'Notes', ph: '' },
  ],
  seedDraft: (data) => ({
    provider: data.insuranceProvider || '', policyNumber: data.policyNumber || data.bookingRef || '',
    hotline: data.hotline || '', coverage: data.coverage || data.summary || '', notes: '',
  }),
  toRaw: (d) => ({
    insuranceProvider: (d.provider || '').trim(), policyNumber: (d.policyNumber || '').trim(),
    hotline: (d.hotline || '').trim(), coverage: (d.coverage || '').trim(), notes: (d.notes || '').trim(),
  }),
  applyScan: applyInsuranceScan,
};
const ROAMING_SCAN_CONFIG = {
  addLabel: 'Add a mobile roaming plan',
  requiredKey: 'carrier',
  draftFields: [
    { key: 'carrier', label: 'Carrier / SIM', ph: 'e.g. Singtel, Airalo eSIM' },
    { key: 'plan', label: 'Plan', ph: 'e.g. 5GB/day at $8/day' },
    { key: 'activation', label: 'Activation Date', ph: '', type: 'date' },
    { key: 'notes', label: 'Notes', ph: '' },
  ],
  seedDraft: (data) => ({
    carrier: data.roamingCarrier || '', plan: data.roamingPlan || '',
    activation: data.activationDate || '', notes: data.summary || '',
  }),
  toRaw: (d) => ({
    roamingCarrier: (d.carrier || '').trim(), roamingPlan: (d.plan || '').trim(),
    activationDate: (d.activation || '').trim(), notes: (d.notes || '').trim(),
  }),
  applyScan: applyRoamingScan,
};

function GenericLegScanPanel({ config, onFill, existing, secondaryLink }) {
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState('screenshot'); // 'screenshot' | 'text' | 'manual'
  const [text, setText] = React.useState('');
  const [file, setFile] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const blankDraft = React.useMemo(() => Object.fromEntries(config.draftFields.map(f => [f.key, ''])), [config]);
  const [manual, setManual] = React.useState(blankDraft);
  const [draft, setDraft] = React.useState(null);
  const [scanned, setScanned] = React.useState(null);
  const fileRef = React.useRef(null);

  const resetAll = () => {
    setError(''); setText(''); setFile(null); setPreview(null); setManual(blankDraft);
    setDraft(null); setScanned(null);
  };

  const applyAndClose = (raw) => {
    onFill(config.applyScan(existing || {}, raw));
    setOpen(false); resetAll();
  };

  const handleFile = React.useCallback((f) => {
    if (!f) return;
    setFile(f); setDraft(null); setScanned(null); setError('');
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  }, []);

  React.useEffect(() => {
    if (!open || mode !== 'screenshot') return;
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          handleFile(item.getAsFile());
          break;
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open, mode, handleFile]);

  const seedDraftFromResult = (data) => {
    setScanned(data);
    setDraft(config.seedDraft(data));
  };

  const handleScan = async () => {
    setLoading(true); setError(''); setDraft(null);
    try {
      let data;
      if (mode === 'screenshot') {
        if (!file) { setError('Please upload a screenshot.'); setLoading(false); return; }
        const base64 = await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = (ev) => res(ev.target.result.split(',')[1]);
          reader.onerror = rej;
          reader.readAsDataURL(file);
        });
        const token = user ? await user.getIdToken() : '';
        const r = await fetch('/api/tp/scan-screenshot', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ imageData: base64, mimeType: file.type || 'image/jpeg' }),
        });
        data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Scan failed');
      } else {
        if (!text.trim()) { setError('Please paste your booking details.'); setLoading(false); return; }
        const r = await fetch('/api/tp/scan-text', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
        });
        data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Extraction failed');
      }
      seedDraftFromResult(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const canApplyDraft = draft?.[config.requiredKey]?.trim();
  const applyDraft = () => {
    if (!canApplyDraft) return;
    applyAndClose({ ...config.toRaw(draft), screenshotUrl: scanned?.screenshotUrl });
  };

  const canApplyManual = manual[config.requiredKey]?.trim();
  const applyManual = () => {
    if (!canApplyManual) return;
    applyAndClose(config.toRaw(manual));
  };

  if (!open) {
    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <button
          onClick={() => setOpen(true)}
          style={{ appearance: 'none', border: 0, background: PALETTE.ink, color: PALETTE.paper, cursor: 'pointer', borderRadius: 8, padding: '8px 14px', fontFamily: 'Inter', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Icon name="plus" size={13} /> {config.addLabel}
        </button>
        <SecondaryLink link={secondaryLink} />
      </div>
    );
  }

  return (
    <div style={{ background: PALETTE.paperDim, borderRadius: 12, border: `0.5px solid ${PALETTE.rule}`, padding: '14px 16px', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {['screenshot', 'text', 'manual'].map(m => (
            <button key={m} onClick={() => { setMode(m); setError(''); }} style={{ appearance: 'none', border: 0, cursor: 'pointer', borderRadius: 6, padding: '5px 10px', fontFamily: 'Inter', fontSize: 11, fontWeight: 600, background: mode === m ? PALETTE.ink : 'transparent', color: mode === m ? PALETTE.paper : PALETTE.inkMute }}>
              {m === 'screenshot' ? 'Screenshot' : m === 'text' ? 'Paste text' : 'Manual'}
            </button>
          ))}
        </div>
        <button onClick={() => { setOpen(false); resetAll(); }} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, padding: 2 }}>
          <Icon name="x" size={14} />
        </button>
      </div>

      {mode === 'screenshot' && !draft && (
        <div onClick={() => fileRef.current?.click()} style={{ border: `1.5px dashed ${PALETTE.rule}`, borderRadius: 8, padding: '13px 14px', cursor: 'pointer', background: PALETTE.paper, textAlign: 'center', marginBottom: 10 }}>
          {preview ? (
            <img src={preview} alt="preview" style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 6, marginBottom: 6 }} />
          ) : (
            <>
              <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft }}>Paste screenshot (Ctrl+V)</div>
              <div style={{ fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute, marginTop: 2 }}>or tap to upload</div>
            </>
          )}
          <input ref={fileRef} type="file" accept="image/*" onChange={e => handleFile(e.target.files?.[0])} style={{ display: 'none' }} />
        </div>
      )}

      {mode === 'text' && !draft && (
        <textarea
          value={text} onChange={e => setText(e.target.value)}
          placeholder="Paste your booking confirmation email or details here…"
          rows={4}
          style={{ width: '100%', boxSizing: 'border-box', appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '9px 12px', fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, background: PALETTE.paper, outline: 'none', resize: 'none', lineHeight: 1.5, marginBottom: 10 }}
        />
      )}

      {mode === 'manual' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
          {config.draftFields.map(f => (
            <div key={f.key}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>{f.label}</div>
              <input type={f.type || 'text'} value={manual[f.key]} onChange={e => setManual(m => ({ ...m, [f.key]: e.target.value }))} placeholder={f.ph} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
            </div>
          ))}
        </div>
      )}

      {error && <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.red, marginBottom: 8 }}>{error}</div>}

      {draft && !error && mode !== 'manual' && (
        <div style={{ background: PALETTE.paper, borderRadius: 8, padding: '10px 12px', marginBottom: 10, border: `0.5px solid ${PALETTE.rule}` }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.green, marginBottom: 8 }}>✓ Extracted — check/edit before adding</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {config.draftFields.map(f => (
              <div key={f.key}>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>{f.label}</div>
                <input type={f.type || 'text'} value={draft[f.key] || ''} onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))} placeholder={f.ph} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paperDim, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === 'manual' ? (
        <button onClick={applyManual} disabled={!canApplyManual} style={{ appearance: 'none', border: 0, cursor: canApplyManual ? 'pointer' : 'default', width: '100%', padding: '10px', background: canApplyManual ? PALETTE.accent : PALETTE.rule, color: canApplyManual ? '#fff' : PALETTE.inkMute, borderRadius: 8, fontFamily: 'Inter', fontSize: 13, fontWeight: 600 }}>
          Add booking
        </button>
      ) : !draft ? (
        <button onClick={handleScan} disabled={loading} style={{ appearance: 'none', border: 0, cursor: loading ? 'default' : 'pointer', width: '100%', padding: '10px', background: loading ? PALETTE.rule : PALETTE.ink, color: loading ? PALETTE.inkMute : PALETTE.paper, borderRadius: 8, fontFamily: 'Inter', fontSize: 13, fontWeight: 600 }}>
          {loading ? 'Extracting…' : 'Extract details'}
        </button>
      ) : (
        <button onClick={applyDraft} disabled={!canApplyDraft} style={{ appearance: 'none', border: 0, cursor: canApplyDraft ? 'pointer' : 'default', width: '100%', padding: '10px', background: canApplyDraft ? PALETTE.accent : PALETTE.rule, color: canApplyDraft ? '#fff' : PALETTE.inkMute, borderRadius: 8, fontFamily: 'Inter', fontSize: 13, fontWeight: 600 }}>
          Add booking
        </button>
      )}
    </div>
  );
}

function InsuranceScanPanel(props) { return <GenericLegScanPanel config={INSURANCE_SCAN_CONFIG} {...props} />; }
function RoamingScanPanel(props) { return <GenericLegScanPanel config={ROAMING_SCAN_CONFIG} {...props} />; }

function mapScanToFields(result, kind) {
  const fieldDefs = BOOKING_DETAIL_FIELDS[kind] || BOOKING_GENERIC_FIELDS;
  const keys = new Set(fieldDefs.map(f => f.key));
  const m = {};
  if (result.bookingRef) {
    if (keys.has('confirmation')) m.confirmation = result.bookingRef;
    if (keys.has('confirmationNo')) m.confirmationNo = result.bookingRef;
    if (keys.has('policyNumber')) m.policyNumber = result.bookingRef;
  }
  if (keys.has('airline') && result.airline) m.airline = result.airline;
  if (keys.has('passengerName') && result.passengerName) m.passengerName = result.passengerName;
  if (keys.has('departure') && (result.departureDate || result.departureTime)) {
    m.departure = [result.departureDate, result.departureTime].filter(Boolean).join(' ');
  }
  if (keys.has('arrival') && (result.arrivalTime || result.returnDate)) {
    m.arrival = [(result.returnDate || result.departureDate), result.arrivalTime].filter(Boolean).join(' ');
  }
  if (keys.has('hotelName') && result.hotelName) m.hotelName = result.hotelName;
  if (keys.has('checkIn') && result.checkInDate) m.checkIn = result.checkInDate;
  if (keys.has('checkOut') && result.checkOutDate) m.checkOut = result.checkOutDate;
  if (keys.has('notes')) {
    const note = result.activityDetails || result.hotelDetails || result.summary;
    if (note) m.notes = note;
  }
  return m;
}

function BookingItem({ item, checked, onToggle, trip, details, onSaveDetails }) {
  const isLegKind = LEG_KINDS.has(item.kind);
  const [expanded, setExpanded] = React.useState(isLegKind);
  const [showDetails, setShowDetails] = React.useState(false);
  const [draft, setDraft] = React.useState(details || {});
  const [uploading, setUploading] = React.useState(false);
  const [confirmDeleteSS, setConfirmDeleteSS] = React.useState(false);
  const fileRef = React.useRef(null);
  const [lightboxSrc, setLightboxSrc] = React.useState(null);
  const draftRef = React.useRef(draft);
  const iconName = { flight: 'plane', hotel: 'bed', accommodation: 'bed', activity: 'ticket', transit: 'car', ticket: 'ticket', insurance: 'shield', roaming: 'wifi' }[item.kind] || 'compass';

  const fields = BOOKING_DETAIL_FIELDS[item.kind] || BOOKING_GENERIC_FIELDS;
  const det = details || {};
  const hasTextDetails = fields.some(f => typeof det[f.key] === 'string' && det[f.key].trim());
  const legs = det.legs || [];
  const hasLegs = legs.length > 0;
  const hasDetails = hasTextDetails || hasLegs || !!det.screenshot;
  // Flights/accommodation with nothing added yet have nothing to hide behind
  // a collapse toggle — just show the "add a booking" flow directly, no
  // chevron. Once there's at least one leg, it collapses/expands normally.
  const alwaysExpanded = isLegKind && !hasLegs;
  const isExpanded = alwaysExpanded || expanded;

  React.useEffect(() => { setDraft(details || {}); draftRef.current = details || {}; }, [JSON.stringify(details)]); // eslint-disable-line
  React.useEffect(() => { draftRef.current = draft; }, [draft]);

  const saveDetails = () => { onSaveDetails?.(draft); setShowDetails(false); };

  const processFile = React.useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const b64 = await compressImage(file);
      const base64Raw = b64.split(',')[1];
      const mimeType = file.type || 'image/jpeg';
      let scanned = {};
      let screenshotSrc = b64;
      try {
        const r = await fetch('/api/tp/scan-screenshot', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageData: base64Raw, mimeType }),
        });
        if (r.ok) {
          const result = await r.json();
          scanned = mapScanToFields(result, item.kind);
          if (result.screenshotUrl) screenshotSrc = result.screenshotUrl;
        }
      } catch (e) { /* silent — fall back to plain screenshot */ }
      const newDraft = { ...draftRef.current, ...scanned, screenshot: screenshotSrc, screenshotName: file.name };
      setDraft(newDraft); onSaveDetails?.(newDraft); draftRef.current = newDraft;
      // Uploading proof of booking IS the "I've booked this" signal — don't
      // make the user separately tick the checkbox after already proving it.
      if (!checked) onToggle?.(true);
    } catch (err) { console.error('Screenshot save failed:', err); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  }, [onSaveDetails, item.kind, checked, onToggle]);

  React.useEffect(() => {
    // Flights/accommodation have their own paste handler via FlightScanPanel/
    // HotelScanPanel; skip here to avoid the document-level handler firing on
    // both items simultaneously.
    if (!expanded || isLegKind) return;
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          processFile(item.getAsFile());
          break;
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [expanded, isLegKind, item.kind, processFile]);

  const handleFile = (e) => processFile(e.target.files?.[0]);

  const deleteScreenshot = () => {
    const nd = { ...draftRef.current };
    delete nd.screenshot; delete nd.screenshotName;
    setDraft(nd); onSaveDetails?.(nd); setConfirmDeleteSS(false);
  };

  const removeLeg = (legId) => {
    const nd = item.kind === 'accommodation' ? removeHotelLeg(draftRef.current, legId)
      : item.kind === 'activity' ? removeActivityLeg(draftRef.current, legId)
      : item.kind === 'insurance' ? removeInsuranceLeg(draftRef.current, legId)
      : item.kind === 'roaming' ? removeRoamingLeg(draftRef.current, legId)
      : removeFlightLeg(draftRef.current, legId);
    setDraft(nd); onSaveDetails?.(nd);
    if (editingLegId === legId) setEditingLegId(null);
  };

  const [editingLegId, setEditingLegId] = React.useState(null);
  const [legDraft, setLegDraft] = React.useState({});
  const startEditLeg = (leg) => { setEditingLegId(leg.id); setLegDraft(leg); };
  const saveEditLeg = () => {
    const nd = item.kind === 'accommodation' ? updateHotelLeg(draftRef.current, editingLegId, legDraft)
      : item.kind === 'activity' ? updateActivityLeg(draftRef.current, editingLegId, legDraft)
      : item.kind === 'insurance' ? updateInsuranceLeg(draftRef.current, editingLegId, legDraft)
      : item.kind === 'roaming' ? updateRoamingLeg(draftRef.current, editingLegId, legDraft)
      : updateFlightLeg(draftRef.current, editingLegId, legDraft);
    setDraft(nd); onSaveDetails?.(nd);
    setEditingLegId(null);
  };

  const ssZone = (
    <div onClick={() => fileRef.current?.click()} style={{ border: `1.5px dashed ${PALETTE.rule}`, borderRadius: 8, padding: '13px 14px', cursor: 'pointer', background: PALETTE.paperDim, textAlign: 'center', marginBottom: 12 }}>
      <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft }}>Paste screenshot (Ctrl+V)</div>
      <div style={{ fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute, marginTop: 2 }}>or tap to upload · auto-fills details</div>
    </div>
  );

  const scanningZone = (
    <div style={{ border: `1.5px dashed ${PALETTE.rule}`, borderRadius: 8, padding: '13px 14px', background: PALETTE.paperDim, textAlign: 'center', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
      <div style={{ width: 13, height: 13, borderRadius: '50%', border: `2px solid ${PALETTE.accent}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
      <span style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft }}>Scanning screenshot…</span>
    </div>
  );

  const fieldInputs = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {fields.map(f => (
        <div key={f.key}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>{f.label}</div>
          <input value={draft[f.key] || ''} onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))} placeholder={f.placeholder} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }} />
        </div>
      ))}
    </div>
  );

  // Each scanned flight/hotel stay is its own leg card — for flights labeled
  // by its actual route (not forced into an "outbound/return" guess that
  // breaks for multi-city trips), for hotels labeled by name + date range —
  // sorted chronologically, independently editable and removable. This is
  // what makes "more than one flight/stay" actually visible instead of being
  // squeezed into one string or silently overwritten.
  const isAccom = item.kind === 'accommodation';
  const isActivity = item.kind === 'activity';
  const isInsurance = item.kind === 'insurance';
  const isRoaming = item.kind === 'roaming';
  const LEG_PALETTE = [
    { bg: '#EFF6FF', border: '#BFDBFE', label: '#3B82F6', mono: '#1E40AF', dark: '#1E3A8A' },
    { bg: '#F5F0FF', border: '#DDD0FA', label: '#8B5CF6', mono: '#6D28D9', dark: '#5B21B6' },
    { bg: '#FFF8EB', border: '#FDE1A8', label: '#D97706', mono: '#B45309', dark: '#92400E' },
    { bg: '#ECFDF5', border: '#A7F3D0', label: '#10B981', mono: '#047857', dark: '#065F46' },
  ];
  const sortedLegs = isAccom ? sortHotelLegsChrono(legs) : (isActivity || isInsurance || isRoaming) ? legs : sortLegsChrono(legs);
  const legEditFields = isAccom ? [
    { key: 'hotelName', label: 'Hotel', ph: 'e.g. Four Seasons Bali' },
    { key: 'checkIn', label: 'Check-in', ph: 'YYYY-MM-DD', type: 'date' },
    { key: 'checkOut', label: 'Check-out', ph: 'YYYY-MM-DD', type: 'date' },
    { key: 'confirmation', label: 'Confirmation No.', ph: 'e.g. HB-20260001' },
    { key: 'notes', label: 'Notes', ph: '' },
  ] : isActivity ? [
    { key: 'title', label: 'What is it?', ph: 'e.g. Sagrada Familia tour' },
    { key: 'date', label: 'Date', ph: 'YYYY-MM-DD', type: 'date' },
    { key: 'time', label: 'Time', ph: 'HH:MM', type: 'time' },
    { key: 'confirmation', label: 'Confirmation No.', ph: 'e.g. AB-20260001' },
    { key: 'notes', label: 'Notes', ph: '' },
  ] : isInsurance ? [
    { key: 'provider', label: 'Provider', ph: 'e.g. FWD, NTUC Income' },
    { key: 'policyNumber', label: 'Policy No.', ph: 'e.g. TRV-20260001' },
    { key: 'hotline', label: 'Emergency Hotline', ph: 'e.g. +65 6322 2888' },
    { key: 'coverage', label: 'Coverage', ph: 'e.g. Medical up to $500K, trip cancellation' },
    { key: 'notes', label: 'Notes', ph: '' },
  ] : isRoaming ? [
    { key: 'carrier', label: 'Carrier / SIM', ph: 'e.g. Singtel, Airalo eSIM' },
    { key: 'plan', label: 'Plan', ph: 'e.g. 5GB/day at $8/day' },
    { key: 'activation', label: 'Activation Date', ph: 'YYYY-MM-DD', type: 'date' },
    { key: 'notes', label: 'Notes', ph: '' },
  ] : [
    { key: 'flightNo', label: 'Flight No.', ph: 'e.g. SQ469' },
    { key: 'airline', label: 'Airline', ph: 'e.g. Singapore Airlines' },
    { key: 'date', label: 'Date', ph: 'YYYY-MM-DD', type: 'date' },
    { key: 'depTime', label: 'Departs', ph: 'HH:MM', type: 'time' },
    { key: 'origin', label: 'From (airport)', ph: 'e.g. SIN' },
    { key: 'arrTime', label: 'Arrives', ph: 'HH:MM', type: 'time' },
    { key: 'dest', label: 'To (airport)', ph: 'e.g. NRT' },
    { key: 'confirmation', label: 'Booking Ref / PNR', ph: 'e.g. ABC123' },
    { key: 'passengerName', label: 'Passenger(s)', ph: 'e.g. Joseph Lee' },
  ];
  const flightLegsView = hasLegs && (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
      {sortedLegs.map((leg, i) => {
        const c = LEG_PALETTE[i % LEG_PALETTE.length];
        const route = isAccom ? '' : [leg.origin, leg.dest].filter(Boolean).join(' → ');
        if (editingLegId === leg.id) {
          return (
            <div key={leg.id} style={{ padding: '10px 10px', borderRadius: 8, background: c.bg, border: `0.5px solid ${c.border}` }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {legEditFields.map(f => (
                  <div key={f.key}>
                    <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: c.mono, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 3 }}>{f.label}</div>
                    <input
                      type={f.type || 'text'}
                      value={legDraft[f.key] || ''}
                      onChange={e => setLegDraft(d => ({ ...d, [f.key]: f.key === 'origin' || f.key === 'dest' ? e.target.value.toUpperCase() : e.target.value }))}
                      placeholder={f.ph}
                      style={{ appearance: 'none', border: `1px solid ${c.border}`, borderRadius: 6, padding: '6px 8px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={saveEditLeg} style={{ appearance: 'none', border: 0, cursor: 'pointer', background: PALETTE.ink, color: PALETTE.paper, borderRadius: 8, padding: '6px 12px', fontFamily: 'Inter', fontSize: 12, fontWeight: 600 }}>Save</button>
                <button onClick={() => setEditingLegId(null)} style={{ appearance: 'none', border: 0, cursor: 'pointer', background: 'transparent', color: PALETTE.inkMute, fontFamily: 'Inter', fontSize: 12, padding: '6px 4px' }}>Cancel</button>
              </div>
            </div>
          );
        }
        const isFlight = !isAccom && !isActivity && !isInsurance && !isRoaming;
        const icon = isAccom ? '🏨' : isActivity ? '🎫' : isInsurance ? '🛡️' : isRoaming ? '📶' : '✈️';
        const primaryLabel = isAccom ? (leg.hotelName || 'Hotel') : isActivity ? (leg.title || 'Activity')
          : isInsurance ? (leg.provider || 'Insurance') : isRoaming ? (leg.carrier || 'Roaming')
          : (route || 'Flight');
        const itemNoun = isAccom ? 'stay' : isActivity ? 'booking' : isInsurance ? 'policy' : isRoaming ? 'plan' : 'flight';
        return (
        <div key={leg.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', borderRadius: 8, background: c.bg, border: `0.5px solid ${c.border}` }}>
          <span style={{ fontSize: 13, flexShrink: 0, marginTop: 2 }}>{icon}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'JetBrains Mono', fontSize: 13, color: c.dark, fontWeight: 700 }}>
                {primaryLabel}
              </span>
              {isFlight && leg.flightNo && <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: c.mono }}>{leg.flightNo}</span>}
              {isFlight && leg.date && <span style={{ fontFamily: 'Inter', fontSize: 11, color: c.label }}>{fmtLegDate(leg.date)}</span>}
              {(isAccom || isActivity) && leg.confirmation && <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: c.mono }}>{leg.confirmation}</span>}
              {isInsurance && leg.policyNumber && <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: c.mono }}>{leg.policyNumber}</span>}
              {isRoaming && leg.plan && <span style={{ fontFamily: 'Inter', fontSize: 11, color: c.label }}>{leg.plan}</span>}
            </div>
            {isAccom && (leg.checkIn || leg.checkOut) && (
              <div style={{ fontFamily: 'Inter', fontSize: 11, color: c.mono, marginTop: 2 }}>
                {[fmtLegDate(leg.checkIn), fmtLegDate(leg.checkOut)].filter(Boolean).join(' → ')}
              </div>
            )}
            {isActivity && (leg.date || leg.time) && (
              <div style={{ fontFamily: 'Inter', fontSize: 11, color: c.mono, marginTop: 2 }}>
                {[leg.date && fmtLegDate(leg.date), leg.time].filter(Boolean).join(' · ')}
              </div>
            )}
            {isInsurance && (leg.coverage || leg.hotline) && (
              <div style={{ fontFamily: 'Inter', fontSize: 11, color: c.mono, marginTop: 2 }}>
                {[leg.coverage, leg.hotline && `Hotline: ${leg.hotline}`].filter(Boolean).join(' · ')}
              </div>
            )}
            {isRoaming && leg.activation && (
              <div style={{ fontFamily: 'Inter', fontSize: 11, color: c.mono, marginTop: 2 }}>
                Activates {fmtLegDate(leg.activation)}
              </div>
            )}
            {isFlight && (leg.depTime || leg.arrTime || leg.airline) && (() => {
              const withTz = (time, code) => {
                if (!time) return '';
                const tz = airportTzLabel(code);
                return tz ? `${time} (${tz})` : time;
              };
              const timeStr = leg.depTime && leg.arrTime
                ? `${withTz(leg.depTime, leg.origin)} → ${withTz(leg.arrTime, leg.dest)}`
                : withTz(leg.depTime || leg.arrTime, leg.depTime ? leg.origin : leg.dest);
              return (
                <div style={{ fontFamily: 'Inter', fontSize: 11, color: c.mono, marginTop: 2 }}>
                  {[timeStr, leg.airline].filter(Boolean).join(' · ')}
                </div>
              );
            })()}
            {/* Ref/passenger — each leg's own, not a shared trip-level field
                (so a cancelled leg can never leave a stale reference behind). */}
            {isFlight && (leg.confirmation || leg.passengerName) && (
              <div style={{ fontFamily: 'Inter', fontSize: 11, color: c.mono, marginTop: 2 }}>
                {[leg.confirmation && `Ref ${leg.confirmation}`, leg.passengerName].filter(Boolean).join(' · ')}
              </div>
            )}
            {(isAccom || isActivity || isInsurance || isRoaming) && leg.notes && (
              <div style={{ fontFamily: 'Inter', fontSize: 11, color: c.mono, marginTop: 2 }}>{leg.notes}</div>
            )}
            {leg.screenshot && (
              <img src={leg.screenshot} alt="screenshot" onClick={() => setLightboxSrc(leg.screenshot)} style={{ maxWidth: 90, maxHeight: 64, borderRadius: 6, marginTop: 6, cursor: 'pointer', display: 'block', border: `0.5px solid ${c.border}`, objectFit: 'cover' }} />
            )}
          </div>
          <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
            <button onClick={() => startEditLeg(leg)} title={`Edit this ${itemNoun}`} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: '#94A3B8', padding: 2 }}>
              <Icon name="pencil" size={13} />
            </button>
            <button onClick={() => removeLeg(leg.id)} title={`Remove this ${itemNoun}`} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: '#94A3B8', padding: 2 }}>
              <Icon name="x" size={13} />
            </button>
          </div>
        </div>
        );
      })}
    </div>
  );
  // Ref/passenger show inside each leg card now (see flightLegsView above) —
  // never as one shared line for the whole item, since that's exactly what
  // used to go stale once every leg it belonged to was gone.
  const flightDetailsSection = (
    <div style={{ marginTop: 12 }}>
      {flightLegsView}
    </div>
  );

  return (
    <div style={{
      background: PALETTE.card, borderRadius: 12,
      border: checked ? `1.5px solid ${PALETTE.green}` : `0.5px solid ${PALETTE.ruleSoft}`,
      overflow: 'hidden', transition: 'border-color 0.2s',
    }}>
      {/* Main row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 14px 0' }}>
        <button
          onClick={() => onToggle(!checked)}
          style={{ appearance: 'none', border: `1.5px solid ${checked ? PALETTE.green : PALETTE.rule}`, cursor: 'pointer', borderRadius: 6, flexShrink: 0, width: 22, height: 22, marginTop: 2, background: checked ? PALETTE.green : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}
        >
          {checked && <Icon name="check" size={12} stroke={2.5} style={{ color: '#fff' }} />}
        </button>
        <div style={{ width: 36, height: 36, borderRadius: 8, background: PALETTE.paperDim, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: checked ? PALETTE.green : PALETTE.ink }}>
          <Icon name={iconName} size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Fraunces', fontSize: 16, letterSpacing: -0.2, color: PALETTE.ink, lineHeight: 1.2 }}>
            {item.title}
          </div>
          <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, marginTop: 3, lineHeight: 1.4 }}>
            {item.detail}
          </div>
        </div>
        {!alwaysExpanded && (
          <button onClick={() => setExpanded(e => !e)} style={{ appearance: 'none', border: 0, cursor: 'pointer', background: 'transparent', color: PALETTE.inkMute, padding: 4, flexShrink: 0 }}>
            <Icon name={expanded ? 'arrowUp' : 'arrowDn'} size={16} />
          </button>
        )}
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div style={{ padding: '0 14px 14px 72px' }}>
          {isLegKind && trip ? (
            <>
              {item.kind === 'flight' && <FlightSearchPanel trip={trip} />}
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `0.5px solid ${PALETTE.ruleSoft}` }}>
                {item.kind === 'flight' ? (
                  <FlightScanPanel existing={det} secondaryLink={item.links?.[0]} onFill={(merged) => { setDraft(merged); onSaveDetails?.(merged); setShowDetails(false); }} />
                ) : item.kind === 'accommodation' ? (
                  <HotelScanPanel existing={det} secondaryLink={item.links?.[0]} onFill={(merged) => { setDraft(merged); onSaveDetails?.(merged); setShowDetails(false); }} />
                ) : item.kind === 'activity' ? (
                  <ActivityScanPanel existing={det} onFill={(merged) => { setDraft(merged); onSaveDetails?.(merged); setShowDetails(false); }} />
                ) : item.kind === 'insurance' ? (
                  <InsuranceScanPanel existing={det} secondaryLink={item.links?.[0]} onFill={(merged) => { setDraft(merged); onSaveDetails?.(merged); setShowDetails(false); }} />
                ) : (
                  <RoamingScanPanel existing={det} secondaryLink={item.links?.[0]} onFill={(merged) => { setDraft(merged); onSaveDetails?.(merged); setShowDetails(false); }} />
                )}
                {flightDetailsSection}
              </div>
            </>
          ) : (
            <>
              {(item.links || []).length > 0 && (
                <div style={{ paddingTop: 12, marginBottom: 4 }}>
                  <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.inkMute, marginBottom: 8 }}>Discover</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {(item.links || []).map((link, i) => (
                      <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, background: PALETTE.paperDim, color: PALETTE.ink, fontFamily: 'Inter', fontSize: 12, fontWeight: 500, border: `0.5px solid ${PALETTE.rule}`, textDecoration: 'none' }}
                        onMouseEnter={e => e.currentTarget.style.background = PALETTE.ruleSoft}
                        onMouseLeave={e => e.currentTarget.style.background = PALETTE.paperDim}
                      >
                        <Icon name="arrow" size={12} /> {link.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Confirmation section */}
              <div style={{ marginTop: 12 }}>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
                {!hasDetails ? (
                  // No details yet — paste zone + form directly
                  <>
                    {uploading ? scanningZone : !draft.screenshot ? ssZone : (
                      <img src={draft.screenshot} alt="screenshot" onClick={() => setLightboxSrc(draft.screenshot)} style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, border: `0.5px solid ${PALETTE.rule}`, cursor: 'pointer', marginBottom: 12, display: 'block' }} />
                    )}
                    {fieldInputs}
                    <button onClick={saveDetails} style={{ appearance: 'none', border: 0, cursor: 'pointer', background: PALETTE.ink, color: PALETTE.paper, borderRadius: 8, padding: '8px 14px', fontFamily: 'Inter', fontSize: 12, fontWeight: 600, marginTop: 10 }}>Save</button>
                  </>
                ) : !showDetails ? (
                  // View mode
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 10 }}>
                      {fields.filter(f => typeof det[f.key] === 'string' && det[f.key].trim()).map(f => (
                        <div key={f.key} style={{ display: 'flex', gap: 8 }}>
                          <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', minWidth: 80, paddingTop: 1 }}>{f.label}</span>
                          {f.key === 'bookingUrl'
                            ? <a href={det[f.key]} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.accent, textDecoration: 'none', wordBreak: 'break-all' }}>{det[f.key]}</a>
                            : <span style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink }}>{f.key === 'notes' ? linkifyText(det[f.key]) : det[f.key]}</span>
                          }
                        </div>
                      ))}
                      {det.screenshot && (
                        <div style={{ marginTop: 4 }}>
                          <img src={det.screenshot} alt="screenshot" onClick={() => setLightboxSrc(det.screenshot)} style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, border: `0.5px solid ${PALETTE.rule}`, cursor: 'pointer', display: 'block' }} />
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
                            {confirmDeleteSS ? (
                              <>
                                <span style={{ fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkSoft }}>Delete screenshot?</span>
                                <button onClick={deleteScreenshot} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'Inter', fontSize: 11, color: PALETTE.red, padding: 0, fontWeight: 600 }}>Delete</button>
                                <button onClick={() => setConfirmDeleteSS(false)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute, padding: 0 }}>Cancel</button>
                              </>
                            ) : (
                              <button onClick={() => setConfirmDeleteSS(true)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute, padding: 0, display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Icon name="x" size={11} /> Delete screenshot
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button onClick={() => { setDraft(det); setShowDetails(true); }} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', padding: 0, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <Icon name="pencil" size={12} /> Edit details
                      </button>
                      <span style={{ color: PALETTE.inkMute, fontSize: 12 }}>·</span>
                      <button onClick={() => fileRef.current?.click()} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', padding: 0, fontFamily: 'Inter', fontSize: 12, color: PALETTE.accent, display: 'flex', alignItems: 'center', gap: 5 }}>
                        {det.screenshot ? 'Replace screenshot' : 'Add screenshot'}
                      </button>
                    </div>
                  </>
                ) : (
                  // Edit mode
                  <>
                    {uploading ? scanningZone : det.screenshot ? (
                      <img src={det.screenshot} alt="screenshot" onClick={() => setLightboxSrc(det.screenshot)} style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, border: `0.5px solid ${PALETTE.rule}`, cursor: 'pointer', marginBottom: 12, display: 'block' }} />
                    ) : ssZone}
                    {fieldInputs}
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button onClick={saveDetails} style={{ appearance: 'none', border: 0, cursor: 'pointer', background: PALETTE.ink, color: PALETTE.paper, borderRadius: 8, padding: '8px 14px', fontFamily: 'Inter', fontSize: 12, fontWeight: 600 }}>Save</button>
                      <button onClick={() => setShowDetails(false)} style={{ appearance: 'none', border: 0, cursor: 'pointer', background: 'transparent', color: PALETTE.inkMute, fontFamily: 'Inter', fontSize: 12, padding: '8px 6px' }}>Cancel</button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {!isExpanded && (
        <div style={{ padding: '8px 14px 12px 72px' }}>
          <button onClick={() => setExpanded(true)} style={{ appearance: 'none', border: 0, cursor: 'pointer', background: 'transparent', padding: 0, fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.accent, letterSpacing: 0.5, textTransform: 'uppercase' }}>
            {isLegKind
              ? (hasDetails ? 'View confirmation →' : item.kind === 'flight' ? 'Add a flight/transport booking →' : item.kind === 'accommodation' ? 'Add a hotel booking →' : 'Add an activity booking →')
              : hasDetails ? 'View confirmation →' : 'Upload screenshot / details →'}
          </button>
        </div>
      )}
      {lightboxSrc && <ImageModal src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
}

// ── DocumentsSection ─────────────────────────────────────────────────────────
async function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function DocumentsSection({ docs = [], onSave, px }) {
  const [open, setOpen] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [editLabel, setEditLabel] = React.useState('');
  const fileRef = React.useRef(null);
  const [lightboxSrc, setLightboxSrc] = React.useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { alert('File too large (max 4 MB). For PDFs, try compressing first.'); return; }
    setUploading(true);
    try {
      let data;
      if (file.type.startsWith('image/')) {
        data = await compressImage(file, 1200);
      } else {
        data = await readFileAsBase64(file);
      }
      const newDoc = { id: `doc_${Math.random().toString(36).slice(2, 9)}`, name: file.name, label: file.name, mimeType: file.type, data };
      onSave([...docs, newDoc]);
    } catch { /* silent */ } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const deleteDoc = (id) => onSave(docs.filter(d => d.id !== id));
  const saveLabel = (id) => { onSave(docs.map(d => d.id === id ? { ...d, label: editLabel.trim() || d.name } : d)); setEditingId(null); };

  return (
    <div style={{ padding: `0 ${px}px 0` }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ appearance: 'none', cursor: 'pointer', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderRadius: 12, background: PALETTE.card, border: `0.5px solid ${PALETTE.ruleSoft}` }}
      >
        <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, textAlign: 'left' }}>
          {docs.length > 0 ? `${docs.length} file${docs.length > 1 ? 's' : ''} uploaded` : 'Boarding passes, confirmations, policies…'}
        </div>
        <Icon name={open ? 'arrowUp' : 'arrowDn'} size={16} style={{ color: PALETTE.inkMute }} />
      </button>

      {open && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {docs.map(doc => (
            <div key={doc.id} style={{ background: PALETTE.card, borderRadius: 10, border: `0.5px solid ${PALETTE.ruleSoft}`, overflow: 'hidden' }}>
              {doc.mimeType?.startsWith('image/') ? (
                <img
                  src={doc.data}
                  alt={doc.label}
                  onClick={() => setLightboxSrc(doc.data)}
                  style={{ width: '100%', maxHeight: 200, objectFit: 'cover', cursor: 'pointer', display: 'block' }}
                />
              ) : (
                <a href={doc.data} download={doc.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', textDecoration: 'none' }}>
                  <span style={{ fontSize: 24 }}>📄</span>
                  <span style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.accent }}>{doc.label}</span>
                </a>
              )}
              <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                {editingId === doc.id ? (
                  <>
                    <input
                      autoFocus value={editLabel} onChange={e => setEditLabel(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && saveLabel(doc.id)}
                      style={{ flex: 1, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, border: `1px solid ${PALETTE.rule}`, borderRadius: 6, padding: '4px 8px', outline: 'none', background: PALETTE.paper }}
                    />
                    <button onClick={() => saveLabel(doc.id)} style={{ appearance: 'none', border: 0, background: PALETTE.accent, color: '#fff', borderRadius: 6, padding: '4px 10px', fontFamily: 'Inter', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Save</button>
                    <button onClick={() => setEditingId(null)} style={{ appearance: 'none', border: 0, background: 'transparent', color: PALETTE.inkMute, fontFamily: 'Inter', fontSize: 12, cursor: 'pointer' }}>✕</button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.label}</span>
                    <button onClick={() => { setEditingId(doc.id); setEditLabel(doc.label); }} style={{ appearance: 'none', border: 0, background: 'transparent', color: PALETTE.inkMute, cursor: 'pointer', fontFamily: 'Inter', fontSize: 11 }}>Rename</button>
                    <button onClick={() => deleteDoc(doc.id)} style={{ appearance: 'none', border: 0, background: 'transparent', color: PALETTE.red, cursor: 'pointer', fontFamily: 'Inter', fontSize: 11 }}>Delete</button>
                  </>
                )}
              </div>
            </div>
          ))}

          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            style={{ appearance: 'none', border: `1.5px dashed ${PALETTE.rule}`, borderRadius: 10, padding: '14px', background: 'transparent', cursor: 'pointer', fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkMute, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <Icon name="plus" size={16} />
            {uploading ? 'Uploading…' : 'Add document or photo'}
          </button>
          <input ref={fileRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={handleFile} />
          <div style={{ fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute, textAlign: 'center' }}>Images and PDFs up to 4 MB</div>
        </div>
      )}
      {lightboxSrc && <ImageModal src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
}

// ── PackingSection ────────────────────────────────────────────────────────────
const PACKING_CATS = ['Documents', 'Clothing', 'Electronics', 'Toiletries', 'Health', 'Misc'];

export function PackingSection({ packing, onChange, px, alwaysOpen = false }) {
  const [open, setOpen] = React.useState(alwaysOpen);
  const [addingCat, setAddingCat] = React.useState('Documents');
  const [query, setQuery] = React.useState('');
  const [packedLast, setPackedLast] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [editText, setEditText] = React.useState('');

  const checkedCount = packing.filter(i => i.checked).length;

  const toggle = (id) => onChange(packing.map(i => i.id === id ? { ...i, checked: !i.checked } : i));
  const remove = (id) => onChange(packing.filter(i => i.id !== id));
  const addItem = () => {
    if (!query.trim()) return;
    onChange([...packing, { id: `pk_${Math.random().toString(36).slice(2, 9)}`, cat: addingCat, text: query.trim(), checked: false, custom: true }]);
    setQuery('');
  };
  const saveEdit = (id) => {
    if (!editText.trim()) return;
    onChange(packing.map(i => i.id === id ? { ...i, text: editText.trim() } : i));
    setEditingId(null);
  };

  const q = query.trim().toLowerCase();
  const visible = q ? packing.filter(i => i.text.toLowerCase().includes(q)) : packing;
  const cats = PACKING_CATS.filter(c => visible.some(i => i.cat === c));

  return (
    <div style={{ padding: `${alwaysOpen ? 0 : 20}px ${px}px 0` }}>
      {!alwaysOpen && (
        <button
          onClick={() => setOpen(o => !o)}
          style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 0, marginBottom: open ? 14 : 0 }}
        >
          <div>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 1.2, textTransform: 'uppercase', textAlign: 'left' }}>Packing list</div>
            <div style={{ fontFamily: 'Fraunces', fontSize: 19, color: PALETTE.ink, letterSpacing: -0.3, marginTop: 3, textAlign: 'left' }}>{checkedCount}/{packing.length} packed</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {packing.length > 0 && (
              <div style={{ width: 48, height: 4, borderRadius: 2, background: PALETTE.ruleSoft, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(checkedCount / packing.length) * 100}%`, background: PALETTE.green, transition: 'width 0.3s' }} />
              </div>
            )}
            <Icon name={open ? 'arrowUp' : 'arrowDn'} size={18} style={{ color: PALETTE.inkMute }} />
          </div>
        </button>
      )}

      {open && (
        <>
          {/* Search or add, in one bar — mirrors the Ideas tab's merged
              search/add omnibox. Category picker + Add button stay compact
              alongside it instead of living in their own block below. */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 7, padding: '7px 11px', background: PALETTE.card, borderRadius: 8, border: `1px solid ${PALETTE.rule}` }}>
              <Icon name={query.trim() ? 'plus' : 'search'} size={13} style={{ color: PALETTE.inkMute, flexShrink: 0 }} />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addItem()}
                placeholder="Search or add a packing item…"
                style={{ flex: 1, minWidth: 0, appearance: 'none', border: 0, outline: 'none', background: 'transparent', fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink }}
              />
              {query && <button onClick={() => setQuery('')} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, padding: 0, lineHeight: 1 }}>✕</button>}
            </div>
            <select
              value={addingCat}
              onChange={e => setAddingCat(e.target.value)}
              style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '0 8px', background: PALETTE.card, fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, outline: 'none', flexShrink: 0, width: 92 }}
            >
              {PACKING_CATS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={addItem} disabled={!query.trim()} style={{
              appearance: 'none', border: 0, cursor: query.trim() ? 'pointer' : 'default', flexShrink: 0,
              background: query.trim() ? PALETTE.ink : PALETTE.rule, color: query.trim() ? PALETTE.paper : PALETTE.inkMute,
              borderRadius: 8, padding: '0 14px', fontFamily: 'Inter', fontSize: 13, fontWeight: 600,
            }}>Add</button>
          </div>

          {packing.length > 0 && (
            <button
              onClick={() => setPackedLast(v => !v)}
              style={{ appearance: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 14, padding: '4px 10px', borderRadius: 999, border: `1px solid ${packedLast ? PALETTE.ink : PALETTE.rule}`, background: packedLast ? PALETTE.ink : 'transparent', color: packedLast ? PALETTE.paper : PALETTE.inkSoft, fontFamily: 'Inter', fontSize: 11, fontWeight: 500 }}
            >
              <Icon name="check" size={11} stroke={2.5} /> Packed last
            </button>
          )}

          {q && visible.length === 0 && (
            <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkMute, padding: '4px 0 16px' }}>No items match — press Enter to add "{query.trim()}".</div>
          )}

          {cats.map(cat => {
            let items = visible.filter(i => i.cat === cat);
            if (packedLast) items = [...items].sort((a, b) => (a.checked ? 1 : 0) - (b.checked ? 1 : 0));
            return (
              <div key={cat} style={{ marginBottom: 16 }}>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
                  {cat}
                </div>
                {items.map(item => (
                  <div key={item.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '7px 0',
                    borderBottom: `0.5px solid ${PALETTE.ruleSoft}`,
                  }}>
                    <button
                      onClick={() => toggle(item.id)}
                      style={{
                        appearance: 'none', border: `1.5px solid ${item.checked ? PALETTE.green : PALETTE.rule}`,
                        cursor: 'pointer', borderRadius: 5, flexShrink: 0,
                        width: 18, height: 18, background: item.checked ? PALETTE.green : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.15s',
                      }}
                    >
                      {item.checked && <Icon name="check" size={10} stroke={2.5} style={{ color: '#fff' }} />}
                    </button>
                    {editingId === item.id ? (
                      <>
                        <input
                          autoFocus
                          value={editText}
                          onChange={e => setEditText(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveEdit(item.id); if (e.key === 'Escape') setEditingId(null); }}
                          style={{
                            flex: 1, appearance: 'none', border: `1px solid ${PALETTE.rule}`,
                            borderRadius: 6, padding: '4px 8px', background: PALETTE.paper,
                            fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, outline: 'none',
                          }}
                        />
                        <button onClick={() => saveEdit(item.id)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.accent, fontFamily: 'Inter', fontSize: 12, fontWeight: 600, padding: '4px 4px' }}>Save</button>
                        <button onClick={() => setEditingId(null)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, fontFamily: 'Inter', fontSize: 12, padding: '4px 2px' }}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <span style={{
                          flex: 1, fontFamily: 'Inter', fontSize: 13,
                          color: item.checked ? PALETTE.inkMute : PALETTE.ink,
                          textDecoration: item.checked ? 'line-through' : 'none',
                        }}>{item.text}</span>
                        <button onClick={() => { setEditingId(item.id); setEditText(item.text); }} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, padding: '2px 4px' }}>
                          <Icon name="pencil" size={12} />
                        </button>
                        <button onClick={() => remove(item.id)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, padding: '2px 4px' }}>
                          <Icon name="x" size={12} />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
